import React, { useState, useEffect, Component } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Badge } from "./components/ui/badge";
import { Progress } from "./components/ui/progress";
import { Alert, AlertDescription } from "./components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { BarChart3, Upload, AlertTriangle, TrendingUp, TrendingDown, Package, Users, Calendar, FileText, PieChart, LogOut, Download, Filter, Database, ChevronLeft, ChevronRight, RefreshCw, Menu, X, Home, Activity, Bell, Settings, Eye, Clock } from "lucide-react";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { Sidebar } from "./components/Sidebar";
import SupportChat from "./components/SupportChat";
import ExpiryAlertModal from "./components/ExpiryAlertModal";
import FarmaciaDashboard from "./components/FarmaciaDashboard";
import HistorialConsigna from "./components/HistorialConsigna";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  RadialLinearScale,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar, Pie, Line, PolarArea, Doughnut } from 'react-chartjs-2';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  RadialLinearScale,
  ChartDataLabels
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============================================
// GLOBAL AXIOS CONFIG - Timeouts y proteccion
// ============================================
axios.defaults.timeout = 30000; // 30s timeout global
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.code === 'ECONNABORTED') {
      console.warn('Request timeout:', error.config?.url);
    }
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('userInfo');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

// ============================================
// VERSION CHECK - Auto-detecta codigo viejo y fuerza recarga
// ============================================
const APP_VERSION = '2.1';
(async () => {
  try {
    const res = await fetch(`${BACKEND_URL}/api/health`);
    const data = await res.json();
    if (data.version && data.version !== APP_VERSION) {
      console.warn(`Version mismatch: frontend=${APP_VERSION}, backend=${data.version}. Reloading...`);
    }
  } catch (e) { /* silently ignore */ }
})();

// ============================================
// ERROR BOUNDARY - Previene crash total de la app
// ============================================
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ 
          display: 'flex', flexDirection: 'column', alignItems: 'center', 
          justifyContent: 'center', minHeight: '100vh', padding: '2rem',
          background: 'linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)', color: 'white'
        }}>
          <div style={{ 
            background: 'rgba(255,255,255,0.1)', borderRadius: '1rem', 
            padding: '2rem', maxWidth: '500px', textAlign: 'center',
            backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)'
          }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>MD Link - Error Temporal</h2>
            <p style={{ marginBottom: '1.5rem', opacity: 0.8 }}>
              Se produjo un error inesperado. Esto puede deberse a una actualización reciente.
            </p>
            <button 
              onClick={() => {
                localStorage.removeItem('token');
                localStorage.removeItem('userInfo');
                window.location.reload();
              }}
              style={{ 
                background: '#3b82f6', color: 'white', border: 'none', 
                padding: '0.75rem 2rem', borderRadius: '0.5rem', cursor: 'pointer',
                fontSize: '1rem', fontWeight: 'bold'
              }}
            >
              Reiniciar Aplicacion
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Helper function to get bodega full name
const getBodegaFullName = (bodegaCode) => {
  const bodegasMap = {
    '16': 'Bodega 16 - HSVP COUNT',
    '20': 'Bodega 20 - H.San Juan',
    '23': 'Bodega 23 - H.Mexico',
    '24': 'Bodega 24 - H.Calderon',
    '30': 'Bodega 30 - HSRA Alajuela',
    '94': 'Bodega 94',
    'HH': 'Bodega HH - Anaquel Heredia',
    '207': 'Bodega 207 - H.Monsenor Sanabria',
    'CF': 'Cuarto Frío',
    'BG': 'Bodega'
  };
  return bodegasMap[bodegaCode] || `Bodega ${bodegaCode}`;
};

const LoginPage = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password
      });
      onLogin(response.data);
      toast.success(`Bienvenido ${response.data.user_info.name}`);
    } catch (error) {
      toast.error("Credenciales inválidas");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white/95 backdrop-blur-xl shadow-2xl border-0">
        <CardHeader className="text-center pb-6 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-lg">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-white/20 rounded-full">
              <BarChart3 className="w-8 h-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold">MD Link Dashboard</CardTitle>
          <CardDescription className="text-blue-100 mt-2">
            Sistema Ejecutivo de Control de Consignas
          </CardDescription>
          <div className="flex items-center justify-center mt-3 text-sm text-blue-100">
            <span>Desarrollado por</span>
            <img 
              src="/mdlink-logo.png" 
              alt="MD Link" 
              className="w-6 h-6 mx-2"
            />
            <span className="font-semibold">MD Link</span>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Input
                type="text"
                placeholder="Usuario"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="w-full"
              />
            </div>
            <div>
              <Input
                type="password"
                placeholder="Contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full"
              />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-blue-900 hover:bg-blue-800">
              {loading ? "Ingresando..." : "Iniciar Sesión"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

const FileUpload = ({ onUploadSuccess, type, title, description }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async () => {
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/upload/${type}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Show detailed success message
      if (response.data.details) {
        const details = response.data.details;
        if (type === 'inventory') {
          toast.success(`✅ Inventario actualizado: ${details.new_items} nuevos, ${details.updated_items} actualizados`);
        } else if (type === 'consumption') {
          toast.success(`✅ Consumos agregados: ${details.new_records} nuevos, ${details.duplicates_skipped} duplicados omitidos`);
        } else {
          toast.success(`✅ ${title} cargado exitosamente`);
        }
      } else {
        toast.success(`✅ ${title} cargado exitosamente`);
      }
      
      // Check for critical stock alerts in response
      if (response.data.critical_check) {
        const criticalData = response.data.critical_check;
        const totalCritical = criticalData.total_critical_items || 0;
        
        if (totalCritical > 0) {
          const zeroStock = criticalData.products_with_zero_stock_count || 0;
          const belowMin = criticalData.critical_products_count || 0;
          const nearExpiry = criticalData.products_near_expiry_count || 0;
          
          let criticalMessage = `🚨 ALERTA: ${totalCritical} productos críticos detectados:\n`;
          if (zeroStock > 0) criticalMessage += `• ${zeroStock} con stock en CERO\n`;
          if (belowMin > 0) criticalMessage += `• ${belowMin} bajo mínimo\n`;
          if (nearExpiry > 0) criticalMessage += `• ${nearExpiry} próximos a vencer (30 días)\n`;
          if (criticalData.email_sent) {
            criticalMessage += '\n✉️ Alerta enviada por correo';
          }
          
          toast.warning(criticalMessage, { duration: 10000 });
        }
      }
      
      setFile(null);
      onUploadSuccess();
    } catch (error) {
      toast.error(`❌ Error al cargar ${title}: ${error.response?.data?.detail || error.message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card className="bg-white/80 backdrop-blur-sm border-slate-200">
      <CardHeader>
        <CardTitle className="text-blue-900 flex items-center gap-2">
          <Upload className="w-5 h-5" />
          {title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Input
          type="file"
          accept=".xlsx,.xls"
          onChange={(e) => setFile(e.target.files[0])}
          className="file:bg-blue-100 file:text-blue-900 file:border-0 file:rounded-md"
        />
        <Button 
          onClick={handleUpload} 
          disabled={!file || uploading}
          className="w-full bg-blue-900 hover:bg-blue-800"
        >
          {uploading ? "Cargando..." : "Subir Archivo"}
        </Button>
      </CardContent>
    </Card>
  );
};

const DashboardStats = ({ stats, fetchProductsByCategory, onCheckCriticalStock, onSyncBitua, userInfo, onShowExpiryModal, managingBodega }) => {
  if (!stats) return null;

  return (
    <div className="space-y-6">
      {/* Información General */}
      <Card className="bg-white/90 backdrop-blur-sm border-slate-200">
        <CardHeader>
          <CardTitle className="text-blue-900 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Información General
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Dashboard Controls */}
          <div className="flex gap-2 flex-wrap mb-4">
            <Button 
              onClick={onCheckCriticalStock}
              className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white shadow-lg"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Verificar Stock
            </Button>
          </div>
          
          <div className="mt-6">
            <div className={`grid grid-cols-1 ${(managingBodega === 'HH' || managingBodega === '207') ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-4`}>
              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-slate-800">{stats.total_products}</div>
                <div className="text-sm text-slate-600">Total de Productos Mínimos</div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-green-800">{stats.total_inventory_items}</div>
                <div className="text-sm text-green-600">Productos en Inventario</div>
              </div>
              {(managingBodega === 'HH' || managingBodega === '207') && (
              <div className="bg-orange-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-orange-800">{stats.total_consumptions}</div>
                <div className="text-sm text-orange-600">Productos Consumidos</div>
              </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Días de Inventario */}
      <Card className="bg-white/90 backdrop-blur-sm border-slate-200">
        <CardHeader>
          <CardTitle className="text-blue-900 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Cantidad de Días en Inventario
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(stats.days_inventory_ranges).map(([range, count]) => (
              <div key={range} className="bg-blue-50 p-4 rounded-lg text-center">
                <div className="text-xl font-bold text-blue-900">{count}</div>
                <div className="text-sm text-blue-700">{range} días</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Vencimientos - Clickeable para abrir modal */}
      <Card 
        className="bg-white/90 backdrop-blur-sm border-slate-200 cursor-pointer hover:shadow-lg transition-all hover:border-orange-300"
        onClick={onShowExpiryModal}
        data-testid="expiry-card"
      >
        <CardHeader>
          <CardTitle className="text-blue-900 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-500" />
            Vencimientos
            <span className="ml-auto text-sm font-normal text-orange-600 flex items-center gap-1">
              <Eye className="w-4 h-4" /> Ver detalle
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(stats.expiry_percentages).map(([period, percentage]) => (
              <div key={period} className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm font-medium">
                    {period === 'menos_6_meses' ? 'Menos de 6 meses' :
                     period === '6_12_meses' ? '6-12 meses' : 'Más de 12 meses'}
                  </span>
                  <span className="text-sm text-slate-600">{percentage}%</span>
                </div>
                <Progress value={percentage} className="h-2" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Productos vs Mínimo */}
      <Card className="bg-white/90 backdrop-blur-sm border-slate-200">
        <CardHeader>
          <CardTitle className="text-blue-900 flex items-center gap-2">
            <Package className="w-5 h-5" />
            Productos en Inventarios vs Mínimos
          </CardTitle>
          <p className="text-sm text-slate-600 mt-2">
            Distribución de productos según su nivel de stock actual comparado con el mínimo establecido. 
            Incluye consumos deducidos del inventario para mostrar el stock real disponible.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            {stats?.stock_vs_minimum && Object.entries(stats.stock_vs_minimum).map(([status, count]) => {
              const labels = {
                'igual_minimo': 'Productos igual a minimo',
                'superior_minimo': 'Productos superior al minimo',
                'inferior_minimo': 'Productos inferior al minimo',
                'en_cero': 'Productos en cero',
                'sobre_stock': 'Productos con sobre stock',
                'productos_minimos_igual_cero': 'Productos Mínimos Igual Cero'
              };
              const colors = {
                'igual_minimo': 'bg-blue-50 text-blue-900',
                'superior_minimo': 'bg-green-50 text-green-900',
                'inferior_minimo': 'bg-yellow-50 text-yellow-900',
                'en_cero': 'bg-red-50 text-red-900',
                'sobre_stock': 'bg-purple-50 text-purple-900',
                'productos_minimos_igual_cero': 'bg-gray-50 text-gray-900'
              };
              
              return (
                <div 
                  key={status} 
                  className={`p-4 rounded-lg text-center cursor-pointer transition-all hover:scale-105 hover:shadow-md ${colors[status]}`}
                  onClick={() => fetchProductsByCategory(status)}
                >
                  <div className="text-xl font-bold">{count}</div>
                  <div className="text-sm">{labels[status]}</div>
                  <div className="text-xs mt-1 opacity-70">Click para ver detalles</div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const TopSkusSection = ({ data }) => {
  if (!data || !data.top_5_skus || data.top_5_skus.length === 0) {
    return (
      <Alert>
        <TrendingUp className="h-4 w-4" />
        <AlertDescription>
          No hay datos de consumo disponibles para mostrar el Top 5 SKUs
        </AlertDescription>
      </Alert>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'low': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'ok': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'critical': return 'Stock Crítico';
      case 'low': return 'Stock Bajo';
      case 'ok': return 'Stock OK';
      default: return 'Sin Info';
    }
  };

  return (
    <Card className="bg-gradient-to-r from-indigo-50 via-blue-50 to-cyan-50 border-blue-200">
      <CardHeader>
        <CardTitle className="text-blue-900 flex items-center gap-2 text-xl">
          <TrendingUp className="w-6 h-6" />
          🏆 Top 5 SKUs Más Consumidos
        </CardTitle>
        <CardDescription className="text-blue-700">
          Productos con mayor actividad de consumo - Análisis de {data.total_consumption_records} registros
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {data.top_5_skus.map((sku, index) => (
            <Card key={sku.sku} className={`relative overflow-hidden transition-all duration-300 hover:shadow-lg hover:scale-105 ${
              index === 0 ? 'bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-300' :
              index === 1 ? 'bg-gradient-to-br from-gray-50 to-slate-50 border-2 border-gray-300' :
              index === 2 ? 'bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300' :
              'bg-white border border-blue-200'
            }`}>
              {/* Ranking Badge */}
              <div className={`absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                index === 0 ? 'bg-yellow-500 text-white' :
                index === 1 ? 'bg-gray-500 text-white' :
                index === 2 ? 'bg-amber-500 text-white' :
                'bg-blue-500 text-white'
              }`}>
                #{index + 1}
              </div>

              {/* Trophy for #1 */}
              {index === 0 && (
                <div className="absolute top-2 left-2 text-yellow-500">
                  🏆
                </div>
              )}

              <CardContent className="p-4">
                <div className="space-y-3">
                  {/* SKU and Product Name */}
                  <div>
                    <h3 className="font-bold text-blue-900 text-lg">{sku.sku}</h3>
                    <p className="text-sm text-gray-600 truncate" title={sku.description}>
                      {sku.description || sku.product_name}
                    </p>
                  </div>

                  {/* Main Metrics */}
                  <div className="flex justify-center">
                    <div className="bg-blue-100 p-3 rounded-lg text-center">
                      <div className="text-2xl font-bold text-blue-900">{sku.total_consumed}</div>
                      <div className="text-sm text-blue-600">Total Consumidos</div>
                    </div>
                  </div>

                  {/* Stock Status */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Stock:</span>
                      <Badge variant="outline" className={getStatusColor(sku.stock_status)}>
                        {sku.current_stock === 1 ? "Disponible" : "No Disponible"}
                      </Badge>
                    </div>
                    
                    {sku.total_units && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Unidades:</span>
                        <span className="text-sm font-bold text-gray-700">{sku.total_units}</span>
                      </div>
                    )}
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Estado:</span>
                      <Badge variant="outline" className={getStatusColor(sku.stock_status)}>
                        {getStatusText(sku.stock_status)}
                      </Badge>
                    </div>

                    {sku.total_minimum > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Mínimo:</span>
                        <span className="text-sm font-bold text-gray-700">{sku.total_minimum}</span>
                      </div>
                    )}
                  </div>

                  {/* Users */}
                  <div className="flex justify-center">
                    <div className="bg-purple-50 p-3 rounded-lg text-center">
                      <div className="text-xl font-bold text-purple-900">{sku.unique_users}</div>
                      <div className="text-sm text-purple-600">Usuarios Únicos</div>
                    </div>
                  </div>

                  {/* Recent Activity */}
                  <div className="space-y-1">
                    <h4 className="text-xs font-semibold text-gray-700 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Actividad Reciente:
                    </h4>
                    <div className="space-y-1">
                      {sku.recent_dates.slice(0, 2).map((date, idx) => (
                        <div key={idx} className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
                          {date}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Additional Info */}
                  {sku.expiry_date && (
                    <div className="text-xs text-gray-500">
                      <strong>Vence:</strong> {sku.expiry_date}
                    </div>
                  )}
                  
                  {sku.batch && (
                    <div className="text-xs text-gray-500">
                      <strong>Lote:</strong> {sku.batch}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Summary Stats */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-4 rounded-lg text-center border border-blue-200">
            <div className="text-2xl font-bold text-blue-900">{data.total_analyzed_skus}</div>
            <div className="text-sm text-blue-600">SKUs Analizados</div>
          </div>
          <div className="bg-white p-4 rounded-lg text-center border border-green-200">
            <div className="text-2xl font-bold text-green-900">{data.total_consumption_records}</div>
            <div className="text-sm text-green-600">Registros de Consumo</div>
          </div>
          <div className="bg-white p-4 rounded-lg text-center border border-purple-200">
            <div className="text-2xl font-bold text-purple-900">
              {data.top_5_skus.reduce((sum, sku) => sum + sku.total_consumed, 0)}
            </div>
            <div className="text-sm text-purple-600">Consumos Top 5</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const InventoryRisk = ({ riskItems }) => {
  if (!riskItems || riskItems.length === 0) {
    return (
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          No hay productos en riesgo de vencimiento
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Card className="bg-white/90 backdrop-blur-sm border-slate-200">
      <CardHeader>
        <CardTitle className="text-red-800 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          Productos en Riesgo de Vencimiento
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>SKU</TableHead>
                <TableHead>Descripción</TableHead>
                <TableHead>Lote</TableHead>
                <TableHead>Fecha Vencimiento</TableHead>
                <TableHead>Días Inventario</TableHead>
                <TableHead>Unidades</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {riskItems.map((item, index) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">{item.sku}</TableCell>
                  <TableCell>{item.description}</TableCell>
                  <TableCell>{item.batch}</TableCell>
                  <TableCell>{item.expiry_date}</TableCell>
                  <TableCell>
                    <Badge variant={item.days_inventory > 120 ? "destructive" : "secondary"}>
                      {item.days_inventory}
                    </Badge>
                  </TableCell>
                  <TableCell>{item.units}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

const DetailedInventory = ({ detailedItems }) => {
  const [searchSku, setSearchSku] = React.useState('');
  
  if (!detailedItems || detailedItems.length === 0) {
    return (
      <Alert>
        <Package className="h-4 w-4" />
        <AlertDescription>
          No hay datos de inventario detallado disponibles
        </AlertDescription>
      </Alert>
    );
  }

  // Filtrar items por SKU
  const filteredItems = detailedItems.filter(item => 
    item.sku.toLowerCase().includes(searchSku.toLowerCase()) ||
    item.description.toLowerCase().includes(searchSku.toLowerCase())
  );

  return (
    <Card className="bg-white/90 backdrop-blur-sm border-slate-200">
      <CardHeader>
        <CardTitle className="text-blue-900 flex items-center gap-2">
          <Package className="w-5 h-5" />
          Detalle de Inventario por SKU
        </CardTitle>
        <div className="mt-4">
          <input
            type="text"
            placeholder="🔍 Buscar por SKU o descripción..."
            value={searchSku}
            onChange={(e) => setSearchSku(e.target.value)}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p className="text-sm text-slate-600 mt-2">
            Mostrando {filteredItems.length} de {detailedItems.length} productos
          </p>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>SKU</TableHead>
                <TableHead>Descripción</TableHead>
                <TableHead>Lote</TableHead>
                <TableHead>Fecha Vencimiento</TableHead>
                <TableHead>Días Inventario</TableHead>
                <TableHead>Stock Disponible</TableHead>
                <TableHead>Consumidos</TableHead>
                <TableHead>EPCs</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Mínimo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredItems.map((item, index) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">{item.sku}</TableCell>
                  <TableCell>{item.description}</TableCell>
                  <TableCell>{item.batch}</TableCell>
                  <TableCell>{item.expiry_date}</TableCell>
                  <TableCell>
                    <Badge variant={item.days_inventory > 120 ? "destructive" : "secondary"}>
                      {item.days_inventory}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={item.units > 0 ? "default" : "destructive"} className="font-bold">
                      {item.units}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={item.consumed_units > 0 ? "outline" : "secondary"}>
                      {item.consumed_units || 0}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {item.epc_count || 1}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={item.units > 0 ? "default" : "destructive"}>
                      {item.units > 0 ? "Disponible" : "Agotado"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={
                      item.units === 0 ? "destructive" :
                      item.units >= item.minimo_establecido ? "default" : "secondary"
                    }>
                      {item.minimo_establecido}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

const MinimosAnalysis = ({ data }) => {
  if (!data) {
    return (
      <Alert>
        <BarChart3 className="h-4 w-4" />
        <AlertDescription>Cargando análisis de mínimos...</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Warehouse Analysis */}
      <Card className="bg-white/90 backdrop-blur-sm border-slate-200">
        <CardHeader>
          <CardTitle className="text-blue-900 flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Análisis de Mínimos por Bodega
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {Object.entries(data.warehouse_analysis).map(([warehouse, analysis]) => (
              <div key={warehouse} className="bg-blue-50 p-4 rounded-lg">
                <div className="text-lg font-bold text-blue-900">
                  Bodega {warehouse.split('_')[1]}
                </div>
                <div className="text-sm text-blue-700 space-y-1">
                  <div>Total SKUs: {analysis.total_products}</div>
                  <div>Cantidad de inventario ideal: {analysis.total_min}</div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Top Products */}
      <Card className="bg-white/90 backdrop-blur-sm border-slate-200">
        <CardHeader>
          <CardTitle className="text-blue-900 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Top 10 Productos con Mayores Mínimos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Total Mínimo</TableHead>
                  <TableHead>B20</TableHead>
                  <TableHead>B23</TableHead>
                  <TableHead>B24</TableHead>
                  <TableHead>B16</TableHead>
                  <TableHead>B30</TableHead>
                  <TableHead>B94</TableHead>
                  <TableHead>B214</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.top_products.map((product, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">{product.codigo}</TableCell>
                    <TableCell>
                      <Badge variant="default" className="bg-blue-100 text-blue-800">
                        {product.total_minimo}
                      </Badge>
                    </TableCell>
                    {Object.entries(product.bodegas).map(([bodega, valor]) => (
                      <TableCell key={bodega}>
                        {valor > 0 ? (
                          <Badge variant="secondary">{valor}</Badge>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="mt-4 text-sm text-gray-600">
            Total de productos con mínimos establecidos: {data.total_products_with_minimums}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const ConsumptionAnalysis = ({ data, timeFilter, setTimeFilter }) => {
  if (!data) {
    return (
      <Alert>
        <Users className="h-4 w-4" />
        <AlertDescription>Cargando análisis de consumos...</AlertDescription>
      </Alert>
    );
  }

  const timeFilterOptions = [
    { value: 'week', label: 'Última Semana', days: 7 },
    { value: 'month', label: 'Último Mes', days: 30 },
    { value: '6months', label: 'Últimos 6 Meses', days: 180 },
    { value: 'all', label: 'Todo el Tiempo', days: null }
  ];

  return (
    <div className="space-y-6">
      {/* Time Filter */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-slate-700">Filtrar por periodo:</span>
            </div>
            <div className="flex gap-2 flex-wrap">
              {timeFilterOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setTimeFilter(option.value)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    timeFilter === option.value
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-white text-slate-600 hover:bg-blue-50 border border-slate-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <Card className="bg-white/90 backdrop-blur-sm border-slate-200">
        <CardHeader>
          <CardTitle className="text-blue-900 flex items-center gap-2">
            <Users className="w-5 h-5" />
            Resumen de Consumos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-green-800">{data.total_consumption_records}</div>
              <div className="text-sm text-green-600">Total Registros</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-purple-800">{data.unique_users}</div>
              <div className="text-sm text-purple-600">Usuarios Únicos</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Top Consumed Products */}
      <Card className="bg-white/90 backdrop-blur-sm border-slate-200">
        <CardHeader>
          <CardTitle className="text-blue-900 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Productos Más Consumidos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>SKU</TableHead>
                  <TableHead>Nombre del Producto</TableHead>
                  <TableHead>Cantidad de productos consumidos</TableHead>
                  <TableHead>Fechas Recientes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.top_consumed_products.map((product, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">{product.sku}</TableCell>
                    <TableCell>{product.product_name}</TableCell>
                    <TableCell>
                      <Badge variant="default" className="bg-green-100 text-green-800">
                        {product.total_consumed}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">
                      {product.consumption_dates.slice(-2).join(', ')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Top Users */}
      <Card className="bg-white/90 backdrop-blur-sm border-slate-200">
        <CardHeader>
          <CardTitle className="text-blue-900 flex items-center gap-2">
            <Users className="w-5 h-5" />
            Usuarios con Mayor Consumo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.top_users.map((user, index) => (
              <div key={index} className="bg-slate-50 p-3 rounded-lg flex justify-between items-center">
                <span className="font-medium">{user.user}</span>
                <Badge variant="secondary">{user.consumption_count} consumos</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const CriticalItemsAnalysis = ({ data }) => {
  const [filteredData, setFilteredData] = useState(data);
  const [selectedFilter, setSelectedFilter] = useState('all'); // Default to all critical items
  const [loading, setLoading] = useState(false);

  // Fetch filtered data from backend
  const fetchCriticalItems = async (filter, forceReload = false) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Agregar timestamp para evitar cache
      const timestamp = forceReload ? `&_t=${Date.now()}` : '';
      const response = await axios.get(`${API}/analytics/critical-items?criticality_filter=${filter}${timestamp}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFilteredData(response.data);
    } catch (error) {
      toast.error("Error al cargar items críticos filtrados");
    } finally {
      setLoading(false);
    }
  };

  // Update data when parent data changes
  useEffect(() => {
    if (data) {
      setFilteredData(data);
    }
  }, [data]);

  // Fetch filtered data when filter changes
  useEffect(() => {
    if (selectedFilter !== 'all') {
      fetchCriticalItems(selectedFilter);
    } else {
      setFilteredData(data);
    }
  }, [selectedFilter, data]);

  if (!data && !filteredData) {
    return (
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>Cargando análisis de insumos críticos...</AlertDescription>
      </Alert>
    );
  }

  const displayData = filteredData || data;

  const getCriticalityColor = (level) => {
    switch (level) {
      case 'zero_stock': return "bg-red-100 text-red-800 border-red-200";
      case 'below_minimum': return "bg-orange-100 text-orange-800 border-orange-200";
      case 'half_minimum': return "bg-orange-100 text-orange-800 border-orange-200";
      case 'critical_time': return "bg-purple-100 text-purple-800 border-purple-200";
      case 'excess': return "bg-blue-100 text-blue-800 border-blue-200";
      default: return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getCriticalityLabel = (level) => {
    switch (level) {
      case 'zero_stock': return "Sin Stock";
      case 'below_minimum': return "Bajo Mínimo";
      case 'half_minimum': return "50% del Mínimo";
      case 'critical_time': return "Tiempo Crítico";
      case 'excess': return "Exceso";
      default: return "Normal";
    }
  };

  return (
    <div className="space-y-6">
      {/* Critical Stats */}
      <Card className="bg-white/90 backdrop-blur-sm border-slate-200">
        <CardHeader>
          <CardTitle className="text-red-800 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Resumen de Insumos Críticos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-500">
              <div className="text-2xl font-bold text-red-800">{displayData.total_zero_stock || 0}</div>
              <div className="text-sm text-red-600">Stock en Cero</div>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-500">
              <div className="text-2xl font-bold text-orange-800">{displayData.total_below_minimum || 0}</div>
              <div className="text-sm text-orange-600">Bajo Mínimo</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-500">
              <div className="text-2xl font-bold text-purple-800">{displayData.total_critical || 0}</div>
              <div className="text-sm text-purple-600">Total Críticos</div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
              <div className="text-2xl font-bold text-blue-800">{displayData.total_excess_inventory || 0}</div>
              <div className="text-sm text-blue-600">Exceso Inventario</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="bg-white/90 backdrop-blur-sm border-slate-200">
        <CardHeader>
          <CardTitle className="text-red-800 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Filtros de Criticidad
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {[
              { value: 'below_minimum', label: 'Bajo Mínimo', color: 'bg-orange-100 text-orange-800 border-orange-300' },
              { value: 'zero_stock', label: 'Sin Stock', color: 'bg-red-100 text-red-800 border-red-300' },
              { value: 'critical', label: 'Todos los Críticos', color: 'bg-purple-100 text-purple-800 border-purple-300' },
              { value: 'all', label: 'Todos', color: 'bg-gray-100 text-gray-800 border-gray-300' }
            ].map((filter) => (
              <button
                key={filter.value}
                onClick={() => setSelectedFilter(filter.value)}
                className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                  selectedFilter === filter.value 
                    ? filter.color + ' shadow-md' 
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Critical Items Table */}
      <Card className="bg-white/90 backdrop-blur-sm border-slate-200">
        <CardHeader>
          <CardTitle className="text-red-800 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Detalle de Insumos Críticos (Filtrado)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Cargando datos filtrados...</div>
          ) : (
            <>
              {/* Search Filter */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="🔍 Buscar por SKU o descripción..."
                  onChange={(e) => {
                    const searchTerm = e.target.value.toLowerCase();
                    if (searchTerm) {
                      const filtered = {
                        ...displayData,
                        critical_items: (data?.critical_items || []).filter(item =>
                          item.sku.toLowerCase().includes(searchTerm) ||
                          item.description.toLowerCase().includes(searchTerm)
                        )
                      };
                      setFilteredData(filtered);
                    } else {
                      setFilteredData(data);
                    }
                  }}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
                <p className="text-sm text-slate-600 mt-2">
                  Mostrando {displayData.critical_items?.length || 0} productos críticos
                </p>
              </div>
              
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>SKU</TableHead>
                      <TableHead>Descripción</TableHead>
                      <TableHead>Lote</TableHead>
                      <TableHead>Stock Disponible</TableHead>
                      <TableHead>Consumidos</TableHead>
                      <TableHead>Mínimo</TableHead>
                      <TableHead>Días Inv.</TableHead>
                      <TableHead>Criticidad</TableHead>
                      <TableHead>Razones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayData.critical_items?.slice(0, 50).map((item, index) => (
                    <TableRow key={index} className={item.is_critical ? "bg-red-50" : ""}>
                      <TableCell className="font-medium">{item.sku}</TableCell>
                      <TableCell className="max-w-xs truncate">{item.description}</TableCell>
                      <TableCell className="font-mono text-xs">{item.lote || 'N/A'}</TableCell>
                      <TableCell>
                        <Badge variant={item.current_stock === 0 ? "destructive" : item.current_stock < item.minimum_level ? "secondary" : "default"} className="font-bold">
                          {item.current_stock || 0}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={item.consumed_units > 0 ? "destructive" : "secondary"}>
                          {item.consumed_units || 0}
                        </Badge>
                      </TableCell>
                      <TableCell>{item.minimum_level || '-'}</TableCell>
                      <TableCell>
                        <Badge variant={item.days_inventory > 120 ? "destructive" : item.days_inventory < 7 ? "destructive" : "secondary"}>
                          {item.days_inventory}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className={`px-2 py-1 rounded text-xs font-medium ${getCriticalityColor(item.criticality_level)}`}>
                          {getCriticalityLabel(item.criticality_level)}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <div className="space-y-1">
                          {item.criticality_reasons?.map((reason, idx) => (
                            <div key={idx} className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded">
                              {reason}
                            </div>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const ActivityLogSection = () => {
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [filters, setFilters] = useState({
    action: '',
    user: '',
    limit: 20
  });
  const [showFilters, setShowFilters] = useState(false);
  const [stats, setStats] = useState(null);

  const fetchActivityLogs = async (page = 1, newFilters = filters) => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams({
        limit: newFilters.limit.toString(),
        offset: ((page - 1) * newFilters.limit).toString()
      });
      
      if (newFilters.action) params.append('action_filter', newFilters.action);
      if (newFilters.user) params.append('user_filter', newFilters.user);
      
      const response = await axios.get(`${API}/activity/logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setActivityLogs(response.data.activity_logs || []);
      setTotalLogs(response.data.total_logs || 0);
      setCurrentPage(page);
      
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      setError('Error cargando logs de actividad');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/activity/logs/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching activity stats:', error);
    }
  };

  useEffect(() => {
    fetchActivityLogs();
    fetchStats();
  }, []);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    fetchActivityLogs(1, newFilters);
  };

  const getActionIcon = (action) => {
    if (action.includes('upload') || action.includes('subir')) return '📁';
    if (action.includes('clear') || action.includes('eliminar')) return '🗑️';
    if (action.includes('login') || action.includes('iniciar')) return '🔑';
    if (action.includes('create') || action.includes('crear')) return '➕';
    if (action.includes('update') || action.includes('actualizar')) return '✏️';
    if (action.includes('consultar')) return '👁️';
    return '📊';
  };
  
  const getActionColor = (action) => {
    if (action.includes('upload') || action.includes('subir')) return 'bg-green-50 border-green-200 text-green-800';
    if (action.includes('clear') || action.includes('eliminar')) return 'bg-red-50 border-red-200 text-red-800';
    if (action.includes('login') || action.includes('iniciar')) return 'bg-blue-50 border-blue-200 text-blue-800';
    if (action.includes('create') || action.includes('crear')) return 'bg-purple-50 border-purple-200 text-purple-800';
    if (action.includes('update') || action.includes('actualizar')) return 'bg-yellow-50 border-yellow-200 text-yellow-800';
    if (action.includes('consultar')) return 'bg-cyan-50 border-cyan-200 text-cyan-800';
    return 'bg-slate-50 border-slate-200 text-slate-800';
  };

  const getActionLabel = (action) => {
    const labels = {
      'upload_minimos': 'Cargar Mínimos',
      'upload_inventory': 'Cargar Inventario',
      'upload_consumption': 'Cargar Consumos',
      'clear_data': 'Limpiar Datos',
      'clear_consumptions': 'Limpiar Consumos',
      'login': 'Inicio de Sesión',
      'consultar_logs': 'Ver Actividad',
      'create_user': 'Crear Usuario',
      'update_user': 'Actualizar Usuario',
      'verify_admin_password': 'Verificar Admin'
    };
    return labels[action] || action.replace('_', ' ').toUpperCase();
  };

  if (loading && activityLogs.length === 0) {
    return (
      <Card className="bg-white/90 backdrop-blur-sm border-slate-200">
        <CardHeader>
          <CardTitle className="text-blue-900 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Logs de Actividad
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-slate-600 mt-2">Cargando actividad...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white/90 backdrop-blur-sm border-slate-200">
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-blue-900 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Logs de Actividad
              {stats && (
                <Badge variant="secondary" className="ml-2">
                  {stats.total_activities} total
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              Historial detallado de todas las operaciones realizadas
              {stats && stats.recent_activity_count > 0 && (
                <span className="ml-2 text-green-600">
                  • {stats.recent_activity_count} actividades en 24h
                </span>
              )}
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="w-4 h-4 mr-1" />
            Filtros
          </Button>
        </div>
      </CardHeader>
      
      {showFilters && (
        <CardContent className="border-b">
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Acción</label>
              <Input
                placeholder="Filtrar por acción..."
                value={filters.action}
                onChange={(e) => handleFilterChange('action', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Usuario</label>
              <Input
                placeholder="Filtrar por usuario..."
                value={filters.user}
                onChange={(e) => handleFilterChange('user', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Resultados</label>
              <select 
                className="w-full p-2 border border-slate-300 rounded-md"
                value={filters.limit}
                onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
              >
                <option value={10}>10 por página</option>
                <option value={20}>20 por página</option>
                <option value={50}>50 por página</option>
              </select>
            </div>
          </div>
        </CardContent>
      )}

      <CardContent>
        {error && (
          <Alert className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {activityLogs.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600">No hay actividad registrada</p>
            <p className="text-sm text-slate-500">Los logs aparecerán aquí cuando realices operaciones</p>
          </div>
        ) : (
          <>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {activityLogs.map((log, index) => {
                const fecha = new Date(log.timestamp).toLocaleString('es-ES', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                });

                return (
                  <div key={log.id || index} className={`p-4 rounded-lg border transition-all hover:shadow-md ${getActionColor(log.accion)}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{getActionIcon(log.accion)}</span>
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                              {getActionLabel(log.accion)}
                            </Badge>
                            <span className="text-sm font-medium text-slate-700">{log.usuario}</span>
                          </div>
                          <div className="text-xs text-slate-500 mt-1">{fecha}</div>
                        </div>
                      </div>
                      {log.registros_afectados > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          {log.registros_afectados} reg.
                        </Badge>
                      )}
                    </div>
                    
                    <div className="text-sm text-slate-700 mb-2">{log.descripcion}</div>
                    
                    {log.tabla_afectada && (
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Database className="w-3 h-3" />
                        <span>Tabla: {log.tabla_afectada}</span>
                        {log.bodega && (
                          <>
                            <span>•</span>
                            <span>Bodega: {log.bodega}</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Paginación */}
            {totalLogs > filters.limit && (
              <div className="flex justify-between items-center mt-6 pt-4 border-t">
                <div className="text-sm text-slate-600">
                  Mostrando {((currentPage - 1) * filters.limit) + 1} - {Math.min(currentPage * filters.limit, totalLogs)} de {totalLogs} logs
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() => fetchActivityLogs(currentPage - 1)}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Anterior
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage * filters.limit >= totalLogs}
                    onClick={() => fetchActivityLogs(currentPage + 1)}
                  >
                    Siguiente
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Estadísticas rápidas */}
            {stats && stats.actions_count && (
              <div className="mt-6 pt-4 border-t">
                <h4 className="text-sm font-medium text-slate-700 mb-3">Resumen de Actividades</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(stats.actions_count).slice(0, 4).map(([action, count]) => (
                    <div key={action} className="bg-slate-50 rounded-lg p-3 text-center">
                      <div className="text-lg">{getActionIcon(action)}</div>
                      <div className="text-sm font-medium text-slate-900">{count}</div>
                      <div className="text-xs text-slate-600">{getActionLabel(action)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

const ChartsSection = ({ stats, minimosAnalysis, consumptionAnalysis, criticalItems, topSkus, replenishmentData }) => {
  if (!stats || !minimosAnalysis || !consumptionAnalysis || !criticalItems) {
    return (
      <Alert>
        <PieChart className="h-4 w-4" />
        <AlertDescription>Cargando datos para gráficas...</AlertDescription>
      </Alert>
    );
  }

  console.log('Chart data:', { stats, minimosAnalysis, consumptionAnalysis, criticalItems, topSkus });

  // Función para descargar Business Intelligence en PDF
  const downloadBusinessIntelligencePDF = async () => {
    try {
      toast.info("Generando Informe de Business Intelligence...");
      
      // Crear un nuevo PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let currentY = 20;

      // Helper para agregar nueva página si es necesario
      const checkPageSpace = (spaceNeeded) => {
        if (currentY + spaceNeeded > pageHeight - 20) {
          pdf.addPage();
          currentY = 20;
        }
      };

      // Cargar y agregar el logo
      const addLogo = () => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = function() {
            try {
              // Crear un canvas para convertir la imagen
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              canvas.width = img.width;
              canvas.height = img.height;
              ctx.drawImage(img, 0, 0);
              
              // Convertir a base64
              const imgData = canvas.toDataURL('image/png');
              
              // Agregar logo en la esquina superior derecha
              const logoWidth = 25;
              const logoHeight = (img.height / img.width) * logoWidth;
              pdf.addImage(imgData, 'PNG', pageWidth - logoWidth - 10, 10, logoWidth, logoHeight);
              
              resolve();
            } catch (error) {
              console.warn('Error loading logo:', error);
              resolve(); // Continuar sin logo si hay error
            }
          };
          img.onerror = function() {
            console.warn('Could not load logo image');
            resolve(); // Continuar sin logo si no se puede cargar
          };
          img.src = '/mdlink-logo.png';
        });
      };

      // Agregar logo
      await addLogo();

      // Portada del informe
      pdf.setFillColor(59, 130, 246); // Azul
      pdf.rect(0, 0, pageWidth, 50, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(22);
      pdf.setFont(undefined, 'bold');
      pdf.text('BUSINESS INTELLIGENCE', pageWidth / 2, 25, { align: 'center' });
      pdf.text('REPORTE DE DATOS', pageWidth / 2, 35, { align: 'center' });
      
      pdf.setTextColor(0, 0, 0);
      currentY = 70;

      // Información del reporte
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'normal');
      const currentDate = new Date().toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      pdf.text(`Fecha de generación: ${currentDate}`, pageWidth / 2, currentY, { align: 'center' });
      
      // Agregar texto "Desarrollado por MD Link" debajo de la fecha
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'italic');
      pdf.setTextColor(100, 100, 100);
      pdf.text('Desarrollado por MD Link', pageWidth / 2, currentY + 8, { align: 'center' });
      pdf.setTextColor(0, 0, 0);
      currentY += 25;

      // 1. RESUMEN GENERAL
      pdf.setFillColor(240, 249, 255);
      pdf.rect(15, currentY - 5, pageWidth - 30, 40, 'F');
      
      pdf.setFontSize(16);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(30, 64, 175);
      pdf.text('1. RESUMEN GENERAL', 20, currentY + 5);
      pdf.setTextColor(0, 0, 0);
      currentY += 15;
      
      pdf.setFontSize(11);
      pdf.setFont(undefined, 'normal');
      
      const totalProductos = stats.total_products || 0;
      const totalInventario = stats.total_inventory_items || 0;
      const totalConsumos = stats.total_consumptions || 0;
      
      pdf.text(`• Total de Productos (SKUs): ${totalProductos}`, 25, currentY); currentY += 6;
      pdf.text(`• Total de Unidades en Inventario: ${totalInventario}`, 25, currentY); currentY += 6;
      pdf.text(`• Total de Consumos Registrados: ${totalConsumos}`, 25, currentY); currentY += 15;

      // 2. DISTRIBUCIÓN DE DÍAS EN INVENTARIO
      checkPageSpace(50);
      pdf.setFillColor(254, 249, 195);
      pdf.rect(15, currentY - 5, pageWidth - 30, 45, 'F');
      
      pdf.setFontSize(16);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(146, 64, 14);
      pdf.text('2. DÍAS DE INVENTARIO', 20, currentY + 5);
      pdf.setTextColor(0, 0, 0);
      currentY += 15;
      
      pdf.setFontSize(11);
      pdf.setFont(undefined, 'normal');
      
      const daysData = stats.days_inventory_ranges || {};
      pdf.text(`• 0-10 días: ${daysData['0-10'] || 0} productos`, 25, currentY); currentY += 6;
      pdf.text(`• 10-25 días: ${daysData['10-25'] || 0} productos`, 25, currentY); currentY += 6;
      pdf.text(`• 25-50 días: ${daysData['25-50'] || 0} productos`, 25, currentY); currentY += 6;
      pdf.text(`• Más de 50 días: ${daysData['+50'] || 0} productos`, 25, currentY); currentY += 15;

      // 3. ANÁLISIS DE CRITICIDAD
      checkPageSpace(50);
      pdf.setFillColor(254, 242, 242);
      pdf.rect(15, currentY - 5, pageWidth - 30, 45, 'F');
      
      pdf.setFontSize(16);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(185, 28, 28);
      pdf.text('3. PRODUCTOS CRÍTICOS', 20, currentY + 5);
      pdf.setTextColor(0, 0, 0);
      currentY += 15;
      
      pdf.setFontSize(11);
      pdf.setFont(undefined, 'normal');
      
      const criticalData = criticalItems.summary || {};
      pdf.text(`• Total de Productos Críticos: ${criticalData.total_critical_items || 0}`, 25, currentY); currentY += 6;
      pdf.text(`• Productos Sin Stock: ${criticalData.zero_stock_items || 0}`, 25, currentY); currentY += 6;
      pdf.text(`• Productos Bajo Mínimo: ${criticalData.below_minimum_items || 0}`, 25, currentY); currentY += 15;

      // 4. STOCK VS MÍNIMOS ESTABLECIDOS
      checkPageSpace(60);
      pdf.setFillColor(236, 253, 245);
      pdf.rect(15, currentY - 5, pageWidth - 30, 55, 'F');
      
      pdf.setFontSize(16);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(21, 128, 61);
      pdf.text('4. STOCK VS MÍNIMOS', 20, currentY + 5);
      pdf.setTextColor(0, 0, 0);
      currentY += 15;
      
      pdf.setFontSize(11);
      pdf.setFont(undefined, 'normal');
      
      const stockVsMin = stats.stock_vs_minimum || {};
      pdf.text(`• Productos Igual al Mínimo: ${stockVsMin.igual_minimo || 0}`, 25, currentY); currentY += 6;
      pdf.text(`• Productos Superior al Mínimo: ${stockVsMin.superior_minimo || 0}`, 25, currentY); currentY += 6;
      pdf.text(`• Productos Inferior al Mínimo: ${stockVsMin.inferior_minimo || 0}`, 25, currentY); currentY += 6;
      pdf.text(`• Productos en Cero: ${stockVsMin.en_cero || 0}`, 25, currentY); currentY += 6;
      pdf.text(`• Productos con Sobre-Stock: ${stockVsMin.sobre_stock || 0}`, 25, currentY); currentY += 6;
      pdf.text(`• Productos con Mínimos Igual Cero: ${stockVsMin.productos_minimos_igual_cero || 0}`, 25, currentY); currentY += 15;

      // 5. ANÁLISIS POR BODEGA
      checkPageSpace(50);
      pdf.setFillColor(248, 250, 252);
      pdf.rect(15, currentY - 5, pageWidth - 30, 45, 'F');
      
      pdf.setFontSize(16);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(71, 85, 105);
      pdf.text('5. ANÁLISIS POR BODEGA', 20, currentY + 5);
      pdf.setTextColor(0, 0, 0);
      currentY += 15;
      
      pdf.setFontSize(11);
      pdf.setFont(undefined, 'normal');
      
      const warehouseData = minimosAnalysis.warehouse_analysis || {};
      const warehouseEntries = Array.isArray(warehouseData) ? warehouseData : Object.entries(warehouseData);
      
      warehouseEntries.forEach((warehouse, index) => {
        checkPageSpace(8);
        const bodegaName = Array.isArray(warehouseData) ? warehouse.bodega : warehouse[0].split('_')[1];
        const totalProductos = Array.isArray(warehouseData) ? warehouse.total_productos : warehouse[1].total_products;
        const totalMinimos = Array.isArray(warehouseData) ? warehouse.total_minimos : warehouse[1].total_min;
        
        pdf.text(`• Bodega ${bodegaName}: ${totalProductos} SKUs, ${totalMinimos} unidades mínimas`, 25, currentY);
        currentY += 6;
      });
      currentY += 10;

      // 6. TOP 5 PRODUCTOS MÁS CONSUMIDOS
      checkPageSpace(50);
      pdf.setFillColor(250, 245, 255);
      pdf.rect(15, currentY - 5, pageWidth - 30, 45, 'F');
      
      pdf.setFontSize(16);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(126, 34, 206);
      pdf.text('6. TOP 5 PRODUCTOS MÁS CONSUMIDOS', 20, currentY + 5);
      pdf.setTextColor(0, 0, 0);
      currentY += 15;
      
      pdf.setFontSize(11);
      pdf.setFont(undefined, 'normal');
      
      const topSkusData = topSkus?.top_5_skus || [];
      if (topSkusData.length > 0) {
        topSkusData.forEach((sku, index) => {
          checkPageSpace(8);
          pdf.text(`${index + 1}. ${sku.sku} - ${sku.total_consumed} consumidos - ${sku.unique_users} usuarios`, 25, currentY);
          currentY += 6;
        });
      } else {
        pdf.text('• No hay datos de consumo disponibles', 25, currentY); currentY += 6;
      }
      currentY += 10;

      // 7. REPOSICIÓN DE INVENTARIO
      if (replenishmentData) {
        checkPageSpace(50);
        pdf.setFillColor(255, 228, 230);
        pdf.rect(15, currentY - 5, pageWidth - 30, 45, 'F');
        
        pdf.setFontSize(16);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(190, 18, 60);
        pdf.text('7. REPOSICIÓN DE INVENTARIO', 20, currentY + 5);
        pdf.setTextColor(0, 0, 0);
        currentY += 15;
        
        pdf.setFontSize(11);
        pdf.setFont(undefined, 'normal');
        
        pdf.text(`• Total de Unidades a Reponer: ${replenishmentData.total_units_to_replenish || 0}`, 25, currentY); currentY += 6;
        pdf.text(`• SKUs que Necesitan Reposición: ${replenishmentData.total_skus_to_replenish || 0}`, 25, currentY); currentY += 6;
        
        const byLevel = replenishmentData.by_level || {};
        pdf.text(`• Sin Stock: ${byLevel.zero_stock?.skus || 0} SKUs (${byLevel.zero_stock?.units || 0} unidades)`, 25, currentY); currentY += 6;
        pdf.text(`• Bajo Mínimo: ${byLevel.below_minimum?.skus || 0} SKUs (${byLevel.below_minimum?.units || 0} unidades)`, 25, currentY); currentY += 15;
      }

      // TABLA DE PRODUCTOS CRÍTICOS (si existen)
      if (replenishmentData && replenishmentData.critical_skus && replenishmentData.critical_skus.length > 0) {
        pdf.addPage();
        currentY = 20;
        
        // Agregar logo en la nueva página
        await addLogo();
        
        pdf.setFontSize(16);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(185, 28, 28);
        pdf.text('DETALLE DE PRODUCTOS QUE NECESITAN REPOSICIÓN', pageWidth / 2, currentY, { align: 'center' });
        pdf.setTextColor(0, 0, 0);
        currentY += 20;
        
        // Headers de la tabla
        pdf.setFontSize(9);
        pdf.setFont(undefined, 'bold');
        pdf.text('SKU', 20, currentY);
        pdf.text('DESCRIPCIÓN', 50, currentY);
        pdf.text('STOCK ACTUAL', 105, currentY);
        pdf.text('MÍNIMO', 130, currentY);
        pdf.text('A REPONER', 155, currentY);
        pdf.text('PRIORIDAD', 175, currentY);
        currentY += 8;
        
        // Línea separadora
        pdf.setDrawColor(200, 200, 200);
        pdf.line(20, currentY - 2, 190, currentY - 2);
        
        pdf.setFont(undefined, 'normal');
        const criticalSkus = replenishmentData.critical_skus.slice(0, 30); // Top 30
        
        // Procesamiento sincrónico de productos críticos
        for (let i = 0; i < criticalSkus.length; i++) {
          const product = criticalSkus[i];
          
          if (currentY > pageHeight - 20) {
            pdf.addPage();
            currentY = 20;
            
            // Agregar logo en cada nueva página
            try {
              await addLogo();
            } catch (e) {
              console.warn('Error adding logo to new page:', e);
            }
            
            // Repetir headers
            pdf.setFont(undefined, 'bold');
            pdf.text('SKU', 20, currentY);
            pdf.text('DESCRIPCIÓN', 50, currentY);
            pdf.text('STOCK ACTUAL', 105, currentY);
            pdf.text('MÍNIMO', 130, currentY);
            pdf.text('A REPONER', 155, currentY);
            pdf.text('PRIORIDAD', 175, currentY);
            currentY += 8;
            pdf.setFont(undefined, 'normal');
          }
          
          const prioridad = product.current_stock === 0 ? 'ALTA' : 
                           product.percentage_of_minimum <= 50 ? 'MEDIA' : 'BAJA';
          
          pdf.text(product.sku.substring(0, 12), 20, currentY);
          pdf.text((product.description || 'Sin descripción').substring(0, 25), 50, currentY);
          pdf.text(product.current_stock.toString(), 105, currentY);
          pdf.text(product.minimum_level.toString(), 130, currentY);
          pdf.text(product.units_needed.toString(), 155, currentY);
          pdf.text(prioridad, 175, currentY);
          currentY += 6;
        }
      }

      // Footer con logo y información
      pdf.setFontSize(8);
      pdf.setFont(undefined, 'italic');
      pdf.setTextColor(100, 100, 100);
      pdf.text('Reporte generado por el sistema Business Intelligence - Desarrollado por MD Link', pageWidth / 2, pageHeight - 10, { align: 'center' });
      pdf.text(`Generado el ${currentDate}`, pageWidth / 2, pageHeight - 5, { align: 'center' });

      // Guardar el PDF
      const fileName = `business_intelligence_datos_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
      toast.success("Reporte de Business Intelligence generado exitosamente");
    } catch (error) {
      console.error('Error generating BI PDF:', error);
      toast.error("Error al generar el reporte");
    }
  };

  // Gráfica divergente de Días de Inventario (usando datos reales)
  const daysInventoryData = {
    labels: ['0-10 días', '10-25 días', '25-50 días', '+50 días'],
    datasets: [
      {
        label: 'Cantidad de Productos',
        data: [
          stats.days_inventory_ranges['0-10'] || 0,
          stats.days_inventory_ranges['10-25'] || 0,
          stats.days_inventory_ranges['25-50'] || 0,
          stats.days_inventory_ranges['+50'] || 0
        ],
        backgroundColor: [
          'rgba(34, 197, 94, 0.8)',
          'rgba(59, 130, 246, 0.8)',
          'rgba(251, 191, 36, 0.8)',
          'rgba(239, 68, 68, 0.8)'
        ],
        borderColor: [
          'rgba(34, 197, 94, 1)',
          'rgba(59, 130, 246, 1)',
          'rgba(251, 191, 36, 1)',
          'rgba(239, 68, 68, 1)'
        ],
        borderWidth: 2
      }
    ]
  };

  // Gráfica divergente de Mínimos por Bodega (usando datos reales)
  const warehouseLabels = Object.keys(minimosAnalysis.warehouse_analysis || {}).map(w => `Bodega ${w.split('_')[1]}`);
  const warehouseProductsData = Object.values(minimosAnalysis.warehouse_analysis || {}).map(w => w.total_products || 0);
  const warehouseAvgData = Object.values(minimosAnalysis.warehouse_analysis || {}).map(w => w.avg_min || 0);
  
  const warehouseData = {
    labels: warehouseLabels,
    datasets: [
      {
        label: 'Productos con Mínimos',
        data: warehouseProductsData,
        backgroundColor: 'rgba(30, 58, 138, 0.8)',
        borderColor: 'rgba(30, 58, 138, 1)',
        borderWidth: 2
      },
      {
        label: 'Promedio Mínimos',
        data: warehouseAvgData,
        backgroundColor: 'rgba(147, 197, 253, 0.8)',
        borderColor: 'rgba(147, 197, 253, 1)',
        borderWidth: 2
      }
    ]
  };

  // Gráfica de criticidad (usando datos reales)
  const criticalityData = {
    labels: ['Total Críticos', 'Stock en Cero', 'Bajo Mínimo', 'Exceso Inventario'],
    datasets: [
      {
        data: [
          criticalItems.total_critical || 0,
          criticalItems.total_zero_stock || 0,
          criticalItems.total_below_minimum || 0,
          criticalItems.total_excess_inventory || 0
        ],
        backgroundColor: [
          'rgba(239, 68, 68, 0.8)',
          'rgba(251, 146, 60, 0.8)',
          'rgba(251, 191, 36, 0.8)',
          'rgba(147, 51, 234, 0.8)'
        ],
        borderColor: [
          'rgba(239, 68, 68, 1)',
          'rgba(251, 146, 60, 1)',
          'rgba(251, 191, 36, 1)',
          'rgba(147, 51, 234, 1)'
        ],
        borderWidth: 2
      }
    ]
  };

  // Gráfica de Top 5 SKUs más consumidos (usando datos reales)
  const topSkusData = topSkus?.top_5_skus || [];
  const topProductsConsumption = {
    labels: topSkusData.map(p => p.sku),
    datasets: [
      {
        label: 'Consumos Totales',
        data: topSkusData.map(p => p.total_consumed || 0),
        backgroundColor: [
          'rgba(255, 215, 0, 0.8)',    // Gold for #1
          'rgba(192, 192, 192, 0.8)',  // Silver for #2
          'rgba(205, 127, 50, 0.8)',   // Bronze for #3
          'rgba(59, 130, 246, 0.8)',   // Blue for #4
          'rgba(147, 51, 234, 0.8)'    // Purple for #5
        ],
        borderColor: [
          'rgba(255, 215, 0, 1)',
          'rgba(192, 192, 192, 1)',
          'rgba(205, 127, 50, 1)',
          'rgba(59, 130, 246, 1)',
          'rgba(147, 51, 234, 1)'
        ],
        borderWidth: 2
      }
    ]
  };

  // Nueva gráfica de reposición de unidades críticas
  const replenishmentChartData = replenishmentData ? {
    labels: ['Sin Stock', 'Bajo Mínimo'],
    datasets: [
      {
        label: 'Unidades a Reponer',
        data: [
          replenishmentData.by_level?.zero_stock?.units || 0,
          replenishmentData.by_level?.below_minimum?.units || 0
        ],
        backgroundColor: [
          'rgba(239, 68, 68, 0.8)',   // Red for zero stock
          'rgba(251, 146, 60, 0.8)'   // Orange for below minimum
        ],
        borderColor: [
          'rgba(239, 68, 68, 1)',
          'rgba(251, 146, 60, 1)'
        ],
        borderWidth: 2
      }
    ]
  } : null;

  // Gráfica divergente de stock vs mínimos (usando datos reales)
  const stockVsMinimumData = {
    labels: ['Igual al Mínimo', 'Superior al Mínimo', 'Inferior al Mínimo', 'En Cero', 'Sobre-Stock', 'Mínimos Igual Cero'],
    datasets: [
      {
        label: 'Cantidad de Productos',
        data: [
          stats.stock_vs_minimum?.['igual_minimo'] || 0,
          stats.stock_vs_minimum?.['superior_minimo'] || 0,
          stats.stock_vs_minimum?.['inferior_minimo'] || 0,
          stats.stock_vs_minimum?.['en_cero'] || 0,
          stats.stock_vs_minimum?.['sobre_stock'] || 0,
          stats.stock_vs_minimum?.['productos_minimos_igual_cero'] || 0
        ],
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(34, 197, 94, 0.8)',
          'rgba(251, 191, 36, 0.8)',
          'rgba(239, 68, 68, 0.8)',
          'rgba(147, 51, 234, 0.8)',
          'rgba(107, 114, 128, 0.8)'
        ],
        borderColor: [
          'rgba(59, 130, 246, 1)',
          'rgba(34, 197, 94, 1)',
          'rgba(251, 191, 36, 1)',
          'rgba(239, 68, 68, 1)',
          'rgba(147, 51, 234, 1)',
          'rgba(107, 114, 128, 1)'
        ],
        borderWidth: 2
      }
    ]
  };

  // Opciones de gráficas con números visibles
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            family: 'Inter, sans-serif',
            size: 12,
            weight: '500'
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        cornerRadius: 8,
        padding: 12
      },
      datalabels: {
        display: true,
        color: 'white',
        font: {
          weight: 'bold',
          size: 14
        },
        formatter: (value, context) => {
          return value > 0 ? value : '';
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(156, 163, 175, 0.1)'
        },
        ticks: {
          font: {
            family: 'Inter, sans-serif',
            size: 11
          }
        }
      },
      x: {
        grid: {
          color: 'rgba(156, 163, 175, 0.1)'
        },
        ticks: {
          font: {
            family: 'Inter, sans-serif',
            size: 11
          }
        }
      }
    }
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          font: {
            family: 'Inter, sans-serif',
            size: 12,
            weight: '500'
          },
          padding: 20,
          usePointStyle: true
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        cornerRadius: 8,
        padding: 12
      },
      datalabels: {
        display: true,
        color: 'white',
        font: {
          weight: 'bold',
          size: 14
        },
        formatter: (value, context) => {
          return value > 0 ? value : '';
        }
      }
    }
  };

  return (
    <div className="space-y-8">
      <Card className="bg-gradient-to-br from-blue-50 via-slate-50 to-indigo-50 border-blue-200 shadow-xl">
        <CardHeader className="text-center pb-6">
          <CardTitle className="text-blue-900 flex items-center justify-center gap-3 text-2xl font-bold">
            <div className="p-2 bg-blue-100 rounded-full">
              <PieChart className="w-8 h-8 text-blue-600" />
            </div>
            <span className="bg-gradient-to-r from-blue-800 to-indigo-800 bg-clip-text text-transparent">
              Análisis Visual de Datos - Gráficas con Números Visibles
            </span>
          </CardTitle>
          <CardDescription className="text-blue-700 text-lg mt-3 max-w-3xl mx-auto">
            Visualización interactiva de todos los indicadores clave del sistema con valores mostrados directamente
          </CardDescription>
          
          {/* Botón de descarga PDF */}
          <div className="flex justify-center mt-6">
            <Button 
              onClick={downloadBusinessIntelligencePDF}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold py-3 px-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-3"
            >
              <Download className="w-5 h-5" />
              📊 Descargar Reporte de Datos BI
            </Button>
          </div>
          
          <div className="w-32 h-1 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-full mx-auto mt-4"></div>
        </CardHeader>
      </Card>

      {/* Nueva sección - Gráfica de Reposición Crítica */}
      {replenishmentData && (
        <Card className="bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 border-red-200 shadow-xl hover:shadow-2xl transition-all duration-300">
          <CardHeader className="pb-4">
            <CardTitle className="text-red-900 flex items-center justify-center gap-3 text-2xl font-bold">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <span className="bg-gradient-to-r from-red-800 to-orange-800 bg-clip-text text-transparent">
                📦 Reposición Crítica de Inventario
              </span>
            </CardTitle>
            <CardDescription className="text-red-700 text-center mt-2 text-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <div className="bg-white/90 backdrop-blur-sm p-4 rounded-xl shadow-lg border border-red-100">
                  <div className="text-3xl font-extrabold text-red-800 mb-1">{replenishmentData.total_units_to_replenish}</div>
                  <div className="text-sm text-red-600 font-medium">Total Unidades a Reponer</div>
                  <div className="w-full h-1 bg-gradient-to-r from-red-200 to-red-400 rounded-full mt-2"></div>
                </div>
                <div className="bg-white/90 backdrop-blur-sm p-4 rounded-xl shadow-lg border border-orange-100">
                  <div className="text-3xl font-extrabold text-orange-800 mb-1">{replenishmentData.total_skus_to_replenish}</div>
                  <div className="text-sm text-orange-600 font-medium">SKUs que Necesitan Reposición</div>
                  <div className="w-full h-1 bg-gradient-to-r from-orange-200 to-orange-400 rounded-full mt-2"></div>
                </div>
              </div>
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-2">
            {/* Tabla detallada de reposición por SKU */}
            <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-red-50 to-orange-50 px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-bold text-red-800 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Detalle de SKUs que Requieren Reposición
                </h3>
                <p className="text-sm text-red-600 mt-1">Cantidades específicas a reponer por cada producto crítico</p>
              </div>
              
              {replenishmentData.critical_skus && replenishmentData.critical_skus.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descripción</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lote</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Stock Actual</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Mínimo Requerido</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Unidades a Reponer</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Prioridad</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {replenishmentData.critical_skus
                        .sort((a, b) => a.percentage_of_minimum - b.percentage_of_minimum) // Ordenar por prioridad (menor % = mayor prioridad)
                        .map((item, index) => {
                          const priorityLevel = item.current_stock === 0 ? "Crítico" : 
                                              item.percentage_of_minimum <= 50 ? "Alto" : "Medio";
                          const priorityColor = item.current_stock === 0 ? "bg-red-100 text-red-800" : 
                                              item.percentage_of_minimum <= 50 ? "bg-orange-100 text-orange-800" : "bg-yellow-100 text-yellow-800";
                          
                          return (
                            <tr key={index} className="hover:bg-gray-50 transition-colors">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">{item.sku}</div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-sm text-gray-900 max-w-xs truncate" title={item.description}>
                                  {item.description || 'Sin descripción'}
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-sm font-mono text-gray-700">{item.lote || 'N/A'}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  item.current_stock === 0 ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
                                }`}>
                                  {item.current_stock}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                <span className="text-sm text-gray-900 font-medium">{item.minimum_level}</span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                <span className="inline-flex px-3 py-1 text-sm font-bold rounded-full bg-red-100 text-red-800">
                                  {item.units_needed}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${priorityColor}`}>
                                  {priorityLevel}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center">
                  <div className="text-gray-400 mb-2">📦</div>
                  <p className="text-gray-500">No hay productos que requieran reposición crítica</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Primera fila - Gráficas divergentes principales */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card className="bg-gradient-to-br from-white to-blue-50 border-blue-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02]">
          <CardHeader className="pb-4">
            <CardTitle className="text-blue-800 text-xl font-bold flex items-center gap-2">
              <div className="w-2 h-8 bg-gradient-to-b from-blue-500 to-blue-700 rounded-full"></div>
              Distribución de Días en Inventario
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80 bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-inner">
              <Bar data={daysInventoryData} options={chartOptions} />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-white to-purple-50 border-purple-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02]">
          <CardHeader className="pb-4">
            <CardTitle className="text-purple-800 text-xl font-bold flex items-center gap-2">
              <div className="w-2 h-8 bg-gradient-to-b from-purple-500 to-purple-700 rounded-full"></div>
              Análisis de Criticidad
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80 bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-inner">
              <Doughnut data={criticalityData} options={pieOptions} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Segunda fila - Análisis por bodegas y consumos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card className="bg-gradient-to-br from-white to-green-50 border-green-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02]">
          <CardHeader className="pb-4">
            <CardTitle className="text-green-800 text-xl font-bold flex items-center gap-2">
              <div className="w-2 h-8 bg-gradient-to-b from-green-500 to-green-700 rounded-full"></div>
              Mínimos por Bodega (Divergente)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80 bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-inner">
              <Bar data={warehouseData} options={chartOptions} />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-white to-amber-50 border-amber-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02]">
          <CardHeader className="pb-4">
            <CardTitle className="text-amber-800 text-xl font-bold flex items-center gap-2">
              <div className="w-2 h-8 bg-gradient-to-b from-amber-500 to-amber-700 rounded-full"></div>
              🏆 Top 5 SKUs Más Consumidos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80 bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-inner">
              <PolarArea data={topProductsConsumption} options={pieOptions} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tercera fila - Stock vs Mínimos */}
      <Card className="bg-gradient-to-br from-white to-indigo-50 border-indigo-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.01]">
        <CardHeader className="pb-4">
          <CardTitle className="text-indigo-800 text-xl font-bold flex items-center gap-2">
            <div className="w-2 h-8 bg-gradient-to-b from-indigo-500 to-indigo-700 rounded-full"></div>
            Stock vs Mínimos Establecidos (Análisis Divergente)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80 bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-inner">
            <Pie data={stockVsMinimumData} options={pieOptions} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Section Component - SUPER ADMIN VIEW
const AdminSection = ({ token, userInfo, onManageBodega, availableBodegas: propBodegas }) => {
  const [adminData, setAdminData] = useState({
    bodegas: [],
    usuarios: [],
    totalesGlobales: null
  });
  const [loading, setLoading] = useState(true);
  const [adminDataReady, setAdminDataReady] = useState(false);  const isMountedRef = React.useRef(true);
  const [activeAdminTab, setActiveAdminTab] = useState('overview');

  
  // Estado para vista de detalle de bodega
  const [selectedBodega, setSelectedBodega] = useState(null);
  const [bodegaDetailData, setBodegaDetailData] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [expandedStatCard, setExpandedStatCard] = useState(null);
  const [expandedBodegaDetail, setExpandedBodegaDetail] = useState(null);
  const [trendData, setTrendData] = useState(null);
  
  // Estados para gestión de usuarios
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    username: '',
    password: '',
    nombre_completo: '',
    email: '',
    bodega: 'HH',
    rol: 'bodega'
  });

  useEffect(() => {
    isMountedRef.current = true;
    fetchAdminData();
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchAdminData = async () => {
    setLoading(true);
    try {
      // Ejecutar ambas peticiones en paralelo
      const [consolidatedResponse, usersResponse] = await Promise.allSettled([
        axios.get(`${API}/admin/dashboard-consolidated`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 60000
        }),
        axios.get(`${API}/admin/users`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000
        })
      ]);
      
      if (!isMountedRef.current) return;
      
      // Procesar respuesta del dashboard
      const data = consolidatedResponse.status === 'fulfilled' 
        ? consolidatedResponse.value.data 
        : null;
      
      // Procesar respuesta de usuarios
      let usuarios = [];
      if (usersResponse.status === 'fulfilled') {
        usuarios = usersResponse.value.data.users || [];
      } else {
        // Intentar fallback solo si falló
        try {
          const fallbackResponse = await axios.get(`${API}/auth/users-info`, { timeout: 5000 });
          usuarios = fallbackResponse.data.users || [];
        } catch (e) {
          usuarios = [];
        }
      }
      
      if (!isMountedRef.current) return;
      
      // Solo actualizar si hay datos nuevos; preservar los anteriores si la petición falló
      if (data) {
        setAdminData(prev => ({
          bodegas: data.bodegas || prev.bodegas || [],
          totalesGlobales: data.totales_globales || prev.totalesGlobales || {},
          periodoConsumos: data.periodo_consumos || prev.periodoConsumos,
          usuarios: usuarios.length > 0 ? usuarios : prev.usuarios || []
        }));
        setAdminDataReady(true);
      } else if (usuarios.length > 0) {
        setAdminData(prev => ({ ...prev, usuarios }));
      }
      
      // Cargar tendencias removido - no bloquear carga principal
      
    } catch (error) {
      console.error('Error obteniendo datos de admin:', error);
      if (isMountedRef.current) {
        toast.error('Error cargando datos de administración');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  // Función para cargar detalle de una bodega específica
  const fetchBodegaDetail = async (codigo) => {
    setLoadingDetail(true);
    setSelectedBodega(codigo);
    try {
      const response = await axios.get(`${API}/admin/bodega/${codigo}/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBodegaDetailData(response.data);
    } catch (error) {
      console.error('Error obteniendo detalle de bodega:', error);
      toast.error('Error cargando detalle de bodega');
    } finally {
      setLoadingDetail(false);
    }
  };

  // Función para volver a la vista general
  const handleBackToOverview = () => {
    setSelectedBodega(null);
    setBodegaDetailData(null);
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setUserForm({
      username: '',
      password: '',
      nombre_completo: '',
      email: '',
      bodega: 'HH',
      rol: 'bodega'
    });
    setShowUserModal(true);
  };

  const handleSaveUser = async () => {
    try {
      if (!userForm.username || !userForm.password || !userForm.nombre_completo) {
        toast.error('Por favor completa todos los campos requeridos');
        return;
      }

      const loadingToast = toast.loading('Guardando usuario...');
      
      // Llamar al endpoint para crear usuario
      const response = await axios.post(
        `${API}/admin/users`,
        {
          username: userForm.username,
          password: userForm.password,
          nombre_completo: userForm.nombre_completo,
          email: userForm.email || `${userForm.username}@mdlink.com`,
          bodega: userForm.bodega,
          rol: userForm.rol
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.dismiss(loadingToast);
      
      if (response.data.success) {
        toast.success(`Usuario ${userForm.username} creado exitosamente`);
        setShowUserModal(false);
        
        // Limpiar formulario
        setUserForm({
          username: '',
          password: '',
          nombre_completo: '',
          email: '',
          bodega: 'HH',
          rol: 'bodega'
        });
        
        // Recargar datos
        fetchAdminData();
      } else {
        toast.error('Error al crear usuario');
      }
    } catch (error) {
      console.error('Error guardando usuario:', error);
      toast.dismiss();
      
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Error al guardar usuario');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="relative mx-auto mb-6 w-20 h-20">
          <div className="w-20 h-20 border-4 border-emerald-200 rounded-full animate-spin border-t-emerald-600"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <Settings className="w-8 h-8 text-emerald-600" />
          </div>
        </div>
        <p className="text-xl font-semibold text-slate-700">Cargando panel de administracion...</p>
        <p className="mt-2 text-sm text-slate-500">
          Obteniendo inventario en tiempo real de todas las bodegas
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <CardHeader>
          <CardTitle className="text-2xl flex items-center gap-3">
            <Settings className="w-8 h-8" />
            Panel de Administración
          </CardTitle>
          <CardDescription className="text-emerald-100">
            Vista global del sistema MD Link - {adminData.bodegas.length} bodegas activas
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Tabs Navigation */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-2 border-b">
            <button
              onClick={() => setActiveAdminTab('overview')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeAdminTab === 'overview'
                  ? 'text-emerald-600 border-b-2 border-emerald-600'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Vista General
              </div>
            </button>
            {!userInfo?.configuracion?.ocultar_usuarios && (
            <button
              onClick={() => setActiveAdminTab('users')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeAdminTab === 'users'
                  ? 'text-emerald-600 border-b-2 border-emerald-600'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Usuarios ({adminData.usuarios.length})
              </div>
            </button>
            )}
            {!userInfo?.configuracion?.ocultar_sistema && (
            <button
              onClick={() => setActiveAdminTab('system')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeAdminTab === 'system'
                  ? 'text-emerald-600 border-b-2 border-emerald-600'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4" />
                Sistema
              </div>
            </button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Overview Tab */}
      {activeAdminTab === 'overview' && !selectedBodega && (
        <>
          {/* Global Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  Estadísticas Globales (Todas las Bodegas)
                </div>
                <button
                  onClick={async () => {
                    try {
                      const resp = await axios.get(`${API}/admin/export-consolidated-report`, {
                        headers: { Authorization: `Bearer ${token}` },
                        responseType: 'blob',
                        timeout: 60000
                      });
                      const url = window.URL.createObjectURL(new Blob([resp.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `Reporte_Consolidado_${new Date().toISOString().slice(0,10)}.xlsx`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                    } catch (e) {
                      alert('Error generando reporte: ' + e.message);
                    }
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-lg transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                  Exportar Excel
                </button>
              </CardTitle>
              {adminData.periodoConsumos && (
                <CardDescription>
                  Período de consumos: {adminData.periodoConsumos.desde} al {adminData.periodoConsumos.hasta}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-3">
                    <Database className="w-8 h-8 text-blue-600" />
                    <div>
                      <p className="text-sm text-slate-600">Total Bodegas</p>
                      {adminDataReady ? (
                        <p className="text-2xl font-bold text-blue-900">{adminData.totalesGlobales?.total_bodegas || 0}</p>
                      ) : (
                        <p className="text-sm text-blue-400 animate-pulse">Cargando...</p>
                      )}
                    </div>
                  </div>
                </div>

                <div 
                  className="p-4 bg-gradient-to-br from-red-50 to-rose-50 rounded-lg border border-red-200 cursor-pointer hover:shadow-lg hover:border-red-400 transition-all"
                  onClick={() => setExpandedStatCard(expandedStatCard === 'en_cero' ? null : 'en_cero')}
                >
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-8 h-8 text-red-600" />
                    <div>
                      <p className="text-sm text-slate-600">Productos en Cero</p>
                      {adminDataReady ? (
                        <p className="text-2xl font-bold text-red-900">{adminData.totalesGlobales?.total_en_cero || 0}</p>
                      ) : (
                        <p className="text-sm text-red-400 animate-pulse">Cargando...</p>
                      )}
                    </div>
                  </div>
                </div>

                <div 
                  className="p-4 bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg border border-orange-200 cursor-pointer hover:shadow-lg hover:border-orange-400 transition-all"
                  onClick={() => setExpandedStatCard(expandedStatCard === 'bajo_minimo' ? null : 'bajo_minimo')}
                >
                  <div className="flex items-center gap-3">
                    <TrendingDown className="w-8 h-8 text-orange-600" />
                    <div>
                      <p className="text-sm text-slate-600">Bajo Mínimo</p>
                      {adminDataReady ? (
                        <p className="text-2xl font-bold text-orange-900">{adminData.totalesGlobales?.total_bajo_minimo || 0}</p>
                      ) : (
                        <p className="text-sm text-orange-400 animate-pulse">Cargando...</p>
                      )}
                    </div>
                  </div>
                </div>

                <div 
                  className="p-4 bg-gradient-to-br from-yellow-50 to-amber-50 rounded-lg border border-yellow-200 cursor-pointer hover:shadow-lg hover:border-yellow-400 transition-all"
                  onClick={() => setExpandedStatCard(expandedStatCard === 'vencimientos' ? null : 'vencimientos')}
                >
                  <div className="flex items-center gap-3">
                    <Clock className="w-8 h-8 text-yellow-600" />
                    <div>
                      <p className="text-sm text-slate-600">Vencidos / Por Vencer</p>
                      {adminDataReady ? (
                        <p className="text-2xl font-bold text-yellow-900">{adminData.totalesGlobales?.total_vencimientos || 0}</p>
                      ) : (
                        <p className="text-sm text-yellow-400 animate-pulse">Cargando...</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Panel de desglose por bodega */}
              {expandedStatCard && (
                <div className="mt-4 p-4 bg-white rounded-lg border border-slate-200 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-slate-700">
                      {expandedStatCard === 'en_cero' && 'Productos en Cero por Bodega'}
                      {expandedStatCard === 'bajo_minimo' && 'Bajo Mínimo por Bodega'}
                      {expandedStatCard === 'vencimientos' && 'Vencidos / Por Vencer por Bodega'}
                    </h4>
                    <button onClick={() => { setExpandedStatCard(null); setExpandedBodegaDetail(null); }} className="text-slate-400 hover:text-slate-600">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
                    {adminData.bodegas
                      ?.filter(b => {
                        const val = expandedStatCard === 'en_cero' ? b.stats?.stock_en_cero :
                                    expandedStatCard === 'bajo_minimo' ? b.stats?.bajo_minimo :
                                    b.stats?.proximos_vencer;
                        return val > 0;
                      })
                      .sort((a, b) => {
                        const key = expandedStatCard === 'en_cero' ? 'stock_en_cero' :
                                    expandedStatCard === 'bajo_minimo' ? 'bajo_minimo' : 'proximos_vencer';
                        return (b.stats?.[key] || 0) - (a.stats?.[key] || 0);
                      })
                      .map(b => {
                        const val = expandedStatCard === 'en_cero' ? b.stats?.stock_en_cero :
                                    expandedStatCard === 'bajo_minimo' ? b.stats?.bajo_minimo :
                                    b.stats?.proximos_vencer;
                        const isSelected = expandedBodegaDetail === b.codigo;
                        const colorClass = expandedStatCard === 'en_cero' 
                          ? (isSelected ? 'bg-red-200 text-red-900 border-red-400 ring-2 ring-red-300' : 'bg-red-100 text-red-800 border-red-200')
                          : expandedStatCard === 'bajo_minimo' 
                          ? (isSelected ? 'bg-orange-200 text-orange-900 border-orange-400 ring-2 ring-orange-300' : 'bg-orange-100 text-orange-800 border-orange-200')
                          : (isSelected ? 'bg-yellow-200 text-yellow-900 border-yellow-400 ring-2 ring-yellow-300' : 'bg-yellow-100 text-yellow-800 border-yellow-200');
                        return (
                          <div 
                            key={b.codigo} 
                            className={`p-2 rounded border ${colorClass} text-center cursor-pointer hover:shadow-md transition-all`}
                            onClick={() => setExpandedBodegaDetail(isSelected ? null : b.codigo)}
                          >
                            <p className="text-xs font-medium truncate">{b.nombre}</p>
                            <p className="text-lg font-bold">{val}</p>
                          </div>
                        );
                      })
                    }
                    {adminData.bodegas?.filter(b => {
                      const val = expandedStatCard === 'en_cero' ? b.stats?.stock_en_cero :
                                  expandedStatCard === 'bajo_minimo' ? b.stats?.bajo_minimo :
                                  b.stats?.proximos_vencer;
                      return val > 0;
                    }).length === 0 && (
                      <p className="text-sm text-slate-500 col-span-full text-center py-2">Sin datos en esta categoría</p>
                    )}
                  </div>

                  {/* Detalle de insumos al hacer clic en una bodega */}
                  {expandedBodegaDetail && (() => {
                    const bodega = adminData.bodegas?.find(b => b.codigo === expandedBodegaDetail);
                    if (!bodega) return null;
                    const insumos = expandedStatCard === 'en_cero' ? bodega.stats?.insumos_en_cero :
                                    expandedStatCard === 'bajo_minimo' ? bodega.stats?.insumos_bajo_minimo :
                                    bodega.stats?.insumos_vencimiento;
                    if (!insumos || insumos.length === 0) return (
                      <p className="mt-3 text-sm text-slate-500 text-center">Sin detalle disponible para esta bodega</p>
                    );
                    
                    const headerColor = expandedStatCard === 'en_cero' ? 'bg-red-600' :
                                       expandedStatCard === 'bajo_minimo' ? 'bg-orange-600' : 'bg-yellow-600';
                    
                    return (
                      <div className="mt-3 border rounded-lg overflow-hidden">
                        <div className={`${headerColor} text-white px-3 py-2 text-sm font-semibold`}>
                          {bodega.nombre} — {insumos.length} insumos
                        </div>
                        <div className="max-h-64 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-slate-50 sticky top-0">
                              <tr>
                                <th className="text-left px-3 py-1.5 text-slate-600 font-medium">SKU</th>
                                <th className="text-left px-3 py-1.5 text-slate-600 font-medium">Insumo</th>
                                <th className="text-left px-3 py-1.5 text-slate-600 font-medium">Lote</th>
                                {expandedStatCard !== 'vencimientos' && (
                                  <>
                                    <th className="text-center px-3 py-1.5 text-slate-600 font-medium">Stock</th>
                                    <th className="text-center px-3 py-1.5 text-slate-600 font-medium">Mínimo</th>
                                  </>
                                )}
                                {expandedStatCard === 'vencimientos' && (
                                  <>
                                    <th className="text-center px-3 py-1.5 text-slate-600 font-medium">Vence</th>
                                    <th className="text-center px-3 py-1.5 text-slate-600 font-medium">Estado</th>
                                  </>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {insumos.map((ins, i) => (
                                <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                  <td className="px-3 py-1.5 font-mono text-xs">{ins.sku}</td>
                                  <td className="px-3 py-1.5 truncate max-w-[200px]">{ins.nombre}</td>
                                  <td className="px-3 py-1.5 font-mono text-xs">{ins.lote || 'N/A'}</td>
                                  {expandedStatCard !== 'vencimientos' && (
                                    <>
                                      <td className="text-center px-3 py-1.5">
                                        <span className={`font-bold ${ins.stock === 0 ? 'text-red-600' : 'text-orange-600'}`}>
                                          {ins.stock}
                                        </span>
                                      </td>
                                      <td className="text-center px-3 py-1.5 text-slate-600">{ins.minimo}</td>
                                    </>
                                  )}
                                  {expandedStatCard === 'vencimientos' && (
                                    <>
                                      <td className="text-center px-3 py-1.5 text-slate-600">{ins.fecha_vence}</td>
                                      <td className="text-center px-3 py-1.5">
                                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                          ins.dias < 0 ? 'bg-red-100 text-red-700' : 
                                          ins.dias <= 30 ? 'bg-orange-100 text-orange-700' : 
                                          'bg-yellow-100 text-yellow-700'
                                        }`}>
                                          {ins.estado}
                                        </span>
                                      </td>
                                    </>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </CardContent>
          </Card>

          {/* ==================== ANÁLISIS DE VALOR ==================== */}
          
          {/* 1. Comparativa Visual entre Bodegas */}
          {adminData.bodegas?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-indigo-600" />
                Comparativa entre Bodegas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <Bar
                  data={{
                    labels: adminData.bodegas
                      .filter(b => (b.stats?.stock_en_cero > 0 || b.stats?.bajo_minimo > 0 || b.stats?.proximos_vencer > 0))
                      .sort((a, b) => (b.stats?.stock_en_cero + b.stats?.bajo_minimo) - (a.stats?.stock_en_cero + a.stats?.bajo_minimo))
                      .map(b => b.nombre?.replace('Bodega ', 'B.').replace('Hospital ', 'H.').replace('Anaquel de ', '')),
                    datasets: [
                      {
                        label: 'En Cero',
                        data: adminData.bodegas
                          .filter(b => (b.stats?.stock_en_cero > 0 || b.stats?.bajo_minimo > 0 || b.stats?.proximos_vencer > 0))
                          .sort((a, b) => (b.stats?.stock_en_cero + b.stats?.bajo_minimo) - (a.stats?.stock_en_cero + a.stats?.bajo_minimo))
                          .map(b => b.stats?.stock_en_cero || 0),
                        backgroundColor: 'rgba(220, 38, 38, 0.8)',
                        borderRadius: 4
                      },
                      {
                        label: 'Bajo Mínimo',
                        data: adminData.bodegas
                          .filter(b => (b.stats?.stock_en_cero > 0 || b.stats?.bajo_minimo > 0 || b.stats?.proximos_vencer > 0))
                          .sort((a, b) => (b.stats?.stock_en_cero + b.stats?.bajo_minimo) - (a.stats?.stock_en_cero + a.stats?.bajo_minimo))
                          .map(b => b.stats?.bajo_minimo || 0),
                        backgroundColor: 'rgba(234, 88, 12, 0.8)',
                        borderRadius: 4
                      },
                      {
                        label: 'Vencidos/Por Vencer',
                        data: adminData.bodegas
                          .filter(b => (b.stats?.stock_en_cero > 0 || b.stats?.bajo_minimo > 0 || b.stats?.proximos_vencer > 0))
                          .sort((a, b) => (b.stats?.stock_en_cero + b.stats?.bajo_minimo) - (a.stats?.stock_en_cero + a.stats?.bajo_minimo))
                          .map(b => b.stats?.proximos_vencer || 0),
                        backgroundColor: 'rgba(202, 138, 4, 0.8)',
                        borderRadius: 4
                      }
                    ]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: 'top', labels: { boxWidth: 12, font: { size: 11 } } },
                      datalabels: {
                        display: true,
                        color: '#fff',
                        font: { weight: 'bold', size: 10 },
                        formatter: (v) => v > 0 ? v : ''
                      }
                    },
                    scales: {
                      x: { stacked: true, grid: { display: false } },
                      y: { stacked: true, beginAtZero: true, grid: { color: 'rgba(0,0,0,0.05)' } }
                    }
                  }}
                />
              </div>
            </CardContent>
          </Card>
          )}

          {/* 2. Top Insumos Más Críticos (en cero en múltiples bodegas) */}
          {adminData.bodegas?.length > 0 && (() => {
            // Calcular insumos que están en cero en múltiples bodegas
            const insumoMap = {};
            adminData.bodegas?.forEach(b => {
              (b.stats?.insumos_en_cero || []).forEach(ins => {
                if (!ins.sku || ins.nombre === 'Desconocido') return;
                const key = ins.sku || ins.nombre;
                if (!insumoMap[key]) {
                  insumoMap[key] = { sku: ins.sku, nombre: ins.nombre, bodegas: [], minimo_total: 0 };
                }
                insumoMap[key].bodegas.push(b.nombre?.replace('Bodega ', 'B.'));
                insumoMap[key].minimo_total += ins.minimo || 0;
              });
            });
            
            const topInsumos = Object.values(insumoMap)
              .filter(i => i.bodegas.length >= 1)
              .sort((a, b) => b.bodegas.length - a.bodegas.length || b.minimo_total - a.minimo_total)
              .slice(0, 15);
            
            if (topInsumos.length === 0) return null;
            
            return (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    Insumos Críticos — Prioridad de Reabastecimiento
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-slate-500 mb-3">Insumos con stock en cero, ordenados por cantidad de bodegas afectadas. Priorice los que afectan más ubicaciones.</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-red-50">
                          <th className="text-left px-3 py-2 text-slate-700 font-semibold">#</th>
                          <th className="text-left px-3 py-2 text-slate-700 font-semibold">SKU</th>
                          <th className="text-left px-3 py-2 text-slate-700 font-semibold">Insumo</th>
                          <th className="text-center px-3 py-2 text-slate-700 font-semibold">Bodegas Afectadas</th>
                          <th className="text-center px-3 py-2 text-slate-700 font-semibold">Mín. Total</th>
                          <th className="text-left px-3 py-2 text-slate-700 font-semibold">Ubicaciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topInsumos.map((ins, i) => (
                          <tr key={i} className={`border-b ${i < 3 ? 'bg-red-50/50' : i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                            <td className="px-3 py-2">
                              <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                                i < 3 ? 'bg-red-600 text-white' : 'bg-slate-200 text-slate-600'
                              }`}>{i + 1}</span>
                            </td>
                            <td className="px-3 py-2 font-mono text-xs">{ins.sku}</td>
                            <td className="px-3 py-2 max-w-[250px] truncate font-medium">{ins.nombre}</td>
                            <td className="text-center px-3 py-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                ins.bodegas.length >= 4 ? 'bg-red-100 text-red-700' :
                                ins.bodegas.length >= 2 ? 'bg-orange-100 text-orange-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {ins.bodegas.length} de {adminData.bodegas?.filter(b => (b.stats?.stock_en_cero > 0 || b.stats?.bajo_minimo > 0)).length || '?'}
                              </span>
                            </td>
                            <td className="text-center px-3 py-2 font-bold text-red-600">{ins.minimo_total}</td>
                            <td className="px-3 py-2 text-xs text-slate-600">{ins.bodegas.join(' | ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* Bodegas Overview con botón Gestionar */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Home className="w-5 h-5 text-emerald-600" />
                Gestión por Bodega
              </CardTitle>
              <CardDescription>
                Selecciona una bodega para ver su detalle completo y gestionarla
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {adminData.bodegas.map((bodega) => (
                  <Card 
                    key={bodega.codigo} 
                    className="border-2 hover:border-emerald-400 transition-all hover:shadow-lg cursor-pointer"
                    data-testid={`bodega-card-${bodega.codigo}`}
                  >
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center text-white font-bold text-sm shadow-lg">
                            {bodega.codigo}
                          </div>
                          <span className="text-sm font-semibold">{bodega.nombre}</span>
                        </span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {/* Indicadores CRÍTICOS prominentes */}
                      <div className="grid grid-cols-2 gap-2 mb-3">
                        {/* Stock en CERO */}
                        <div className={`p-3 rounded-lg text-center transition-all ${
                          (bodega.stats?.stock_en_cero || 0) > 0 
                            ? 'bg-gradient-to-br from-red-500 to-red-600 text-white shadow-lg animate-pulse' 
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          <div className="text-2xl font-bold">{bodega.stats?.stock_en_cero || 0}</div>
                          <div className="text-xs font-medium">
                            {(bodega.stats?.stock_en_cero || 0) > 0 ? '🚨 EN CERO' : 'En Cero'}
                          </div>
                        </div>
                        {/* Bajo Mínimo */}
                        <div className={`p-3 rounded-lg text-center transition-all ${
                          (bodega.stats?.bajo_minimo || 0) > 0 
                            ? 'bg-gradient-to-br from-orange-500 to-amber-500 text-white shadow-lg' 
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          <div className="text-2xl font-bold">{bodega.stats?.bajo_minimo || 0}</div>
                          <div className="text-xs font-medium">
                            {(bodega.stats?.bajo_minimo || 0) > 0 ? '⚠️ BAJO MÍNIMO' : 'Bajo Mínimo'}
                          </div>
                        </div>
                      </div>
                      
                      {/* Info adicional compacta */}
                      <div className={`grid ${(bodega.codigo === 'HH' || bodega.codigo === '207') ? 'grid-cols-2' : 'grid-cols-1'} gap-2 text-xs`}>
                        <div className="flex justify-between items-center p-2 bg-green-50 rounded">
                          <span className="text-slate-600">Inventario</span>
                          <span className="font-bold text-green-700">{bodega.stats?.items_inventario || 0}</span>
                        </div>
                        {(bodega.codigo === 'HH' || bodega.codigo === '207') && (
                        <div className="flex justify-between items-center p-2 bg-purple-50 rounded">
                          <span className="text-slate-600">Consumos</span>
                          <span className="font-bold text-purple-700">{bodega.stats?.consumos_ultima_semana || 0}/sem</span>
                        </div>
                        )}
                      </div>
                      
                      {/* Alerta de vencimientos si hay */}
                      {(bodega.stats?.proximos_vencer || 0) > 0 && (
                        <div className="flex justify-between items-center p-2 bg-yellow-100 rounded-lg border border-yellow-300">
                          <span className="text-xs text-yellow-800 font-medium">⏰ Próximos a Vencer</span>
                          <Badge className="bg-yellow-500 text-white">{bodega.stats.proximos_vencer}</Badge>
                        </div>
                      )}
                      
                      {/* Botón único de Gestión */}
                      <Button 
                        className="w-full bg-emerald-600 hover:bg-emerald-700 mt-2"
                        onClick={() => onManageBodega(bodega.codigo)}
                        data-testid={`gestionar-bodega-${bodega.codigo}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Ver Bodega
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Action Button */}
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Actualizar Datos</h3>
                  <p className="text-sm text-slate-600">
                    Recargar estadísticas de todas las bodegas
                  </p>
                </div>
                <Button
                  onClick={fetchAdminData}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Actualizar
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Vista de Detalle de Bodega Seleccionada */}
      {activeAdminTab === 'overview' && selectedBodega && (
        <>
          {loadingDetail ? (
            <Card className="p-8">
              <div className="flex items-center justify-center">
                <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
                <span className="ml-3 text-lg">Cargando detalle de bodega...</span>
              </div>
            </Card>
          ) : bodegaDetailData ? (
            <>
              {/* Header con botón Volver */}
              <Card className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-2xl flex items-center gap-3">
                        <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center font-bold">
                          {bodegaDetailData.bodega?.codigo}
                        </div>
                        {bodegaDetailData.bodega?.nombre}
                      </CardTitle>
                      <CardDescription className="text-emerald-100 mt-2">
                        {bodegaDetailData.bodega?.descripcion || 'Vista detallada de inventario y consumos'}
                      </CardDescription>
                    </div>
                    <Button 
                      onClick={handleBackToOverview}
                      variant="outline"
                      className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                      data-testid="btn-volver-overview"
                    >
                      <ChevronLeft className="w-4 h-4 mr-2" />
                      Volver a Vista General
                    </Button>
                  </div>
                </CardHeader>
              </Card>

              {/* Stats de la Bodega */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Package className="w-8 h-8 text-green-600" />
                      <div>
                        <p className="text-sm text-slate-600">Items en Inventario</p>
                        <p className="text-2xl font-bold text-green-900">
                          {bodegaDetailData.stats?.items_inventario || 0}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <BarChart3 className="w-8 h-8 text-blue-600" />
                      <div>
                        <p className="text-sm text-slate-600">Productos Únicos</p>
                        <p className="text-2xl font-bold text-blue-900">
                          {bodegaDetailData.stats?.productos_unicos || 0}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <TrendingUp className="w-8 h-8 text-purple-600" />
                      <div>
                        <p className="text-sm text-slate-600">Consumos Totales</p>
                        <p className="text-2xl font-bold text-purple-900">
                          {bodegaDetailData.stats?.consumos_totales || 0}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Activity className="w-8 h-8 text-orange-600" />
                      <div>
                        <p className="text-sm text-slate-600">Consumos Semana</p>
                        <p className="text-2xl font-bold text-orange-900">
                          {bodegaDetailData.stats?.consumos_ultima_semana || 0}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Stock vs Mínimos */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    Análisis Stock vs Mínimos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="p-3 bg-red-50 rounded-lg border border-red-200 text-center">
                      <p className="text-2xl font-bold text-red-700">{bodegaDetailData.stats?.stock_vs_minimos?.en_cero || 0}</p>
                      <p className="text-xs text-red-600">Stock en Cero</p>
                    </div>
                    <div className="p-3 bg-orange-50 rounded-lg border border-orange-200 text-center">
                      <p className="text-2xl font-bold text-orange-700">{bodegaDetailData.stats?.stock_vs_minimos?.bajo_minimo || 0}</p>
                      <p className="text-xs text-orange-600">Bajo Mínimo</p>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-center">
                      <p className="text-2xl font-bold text-blue-700">{bodegaDetailData.stats?.stock_vs_minimos?.igual_minimo || 0}</p>
                      <p className="text-xs text-blue-600">Igual al Mínimo</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200 text-center">
                      <p className="text-2xl font-bold text-green-700">{bodegaDetailData.stats?.stock_vs_minimos?.sobre_minimo || 0}</p>
                      <p className="text-xs text-green-600">Sobre Mínimo</p>
                    </div>
                    <div className="p-3 bg-purple-50 rounded-lg border border-purple-200 text-center">
                      <p className="text-2xl font-bold text-purple-700">{bodegaDetailData.stats?.stock_vs_minimos?.sobre_stock || 0}</p>
                      <p className="text-xs text-purple-600">Sobre-Stock</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Productos Críticos */}
              {bodegaDetailData.productos_criticos && bodegaDetailData.productos_criticos.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-red-700">
                      <AlertTriangle className="w-5 h-5" />
                      Productos Críticos ({bodegaDetailData.productos_criticos.length})
                    </CardTitle>
                    <CardDescription>
                      Productos que requieren atención inmediata
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>SKU</TableHead>
                          <TableHead>Nombre</TableHead>
                          <TableHead>Stock Actual</TableHead>
                          <TableHead>Mínimo</TableHead>
                          <TableHead>Estado</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bodegaDetailData.productos_criticos.slice(0, 10).map((producto, index) => (
                          <TableRow key={index} className={producto.criticidad === 'en_cero' ? 'bg-red-50' : 'bg-orange-50'}>
                            <TableCell className="font-medium">{producto.sku}</TableCell>
                            <TableCell className="max-w-xs truncate">{producto.nombre}</TableCell>
                            <TableCell>
                              <Badge variant={producto.stock === 0 ? "destructive" : "secondary"}>
                                {producto.stock}
                              </Badge>
                            </TableCell>
                            <TableCell>{producto.minimo}</TableCell>
                            <TableCell>
                              <Badge variant={producto.criticidad === 'en_cero' ? "destructive" : "outline"} className={producto.criticidad === 'en_cero' ? '' : 'bg-orange-100 text-orange-700'}>
                                {producto.criticidad === 'en_cero' ? 'Sin Stock' : 'Bajo Mínimo'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Consumos Recientes */}
              {bodegaDetailData.consumos_recientes && bodegaDetailData.consumos_recientes.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-purple-600" />
                      Últimos Consumos
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Fecha</TableHead>
                          <TableHead>SKU</TableHead>
                          <TableHead>Usuario</TableHead>
                          <TableHead>EPC</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bodegaDetailData.consumos_recientes.slice(0, 10).map((consumo, index) => (
                          <TableRow key={index}>
                            <TableCell className="text-sm">
                              {new Date(consumo.fecha).toLocaleDateString('es-ES', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </TableCell>
                            <TableCell className="font-medium">{consumo.sku}</TableCell>
                            <TableCell>{consumo.usuario || 'N/A'}</TableCell>
                            <TableCell className="text-xs text-slate-500">{consumo.epc?.slice(-8) || 'N/A'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Días en Inventario */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    Distribución por Días en Inventario
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-3">
                    <div className="p-3 bg-green-50 rounded-lg text-center">
                      <p className="text-xl font-bold text-green-700">{bodegaDetailData.stats?.dias_inventario?.['0-10'] || 0}</p>
                      <p className="text-xs text-green-600">0-10 días</p>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg text-center">
                      <p className="text-xl font-bold text-blue-700">{bodegaDetailData.stats?.dias_inventario?.['10-25'] || 0}</p>
                      <p className="text-xs text-blue-600">10-25 días</p>
                    </div>
                    <div className="p-3 bg-yellow-50 rounded-lg text-center">
                      <p className="text-xl font-bold text-yellow-700">{bodegaDetailData.stats?.dias_inventario?.['25-50'] || 0}</p>
                      <p className="text-xs text-yellow-600">25-50 días</p>
                    </div>
                    <div className="p-3 bg-red-50 rounded-lg text-center">
                      <p className="text-xl font-bold text-red-700">{bodegaDetailData.stats?.dias_inventario?.['+50'] || 0}</p>
                      <p className="text-xs text-red-600">+50 días</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="p-8">
              <div className="text-center text-slate-500">
                <AlertTriangle className="w-12 h-12 mx-auto mb-4" />
                <p>No se pudieron cargar los datos de la bodega</p>
                <Button onClick={handleBackToOverview} className="mt-4">
                  Volver a Vista General
                </Button>
              </div>
            </Card>
          )}
        </>
      )}

      {/* Users Tab */}
      {activeAdminTab === 'users' && !userInfo?.configuracion?.ocultar_usuarios && (
        <>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-emerald-600" />
                  Gestión de Usuarios
                </CardTitle>
                <CardDescription>
                  Total de usuarios en el sistema: {adminData.usuarios.length}
                </CardDescription>
              </div>
              <Button
                onClick={handleCreateUser}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Users className="w-4 h-4 mr-2" />
                Crear Usuario
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Bodega</TableHead>
                    <TableHead>Rol</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {adminData.usuarios.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                        No hay usuarios en el sistema
                      </TableCell>
                    </TableRow>
                  ) : (
                    adminData.usuarios.map((usuario) => (
                      <TableRow key={usuario.username}>
                        <TableCell className="font-medium">{usuario.username}</TableCell>
                        <TableCell>{usuario.name}</TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {usuario.email || 'Sin email'}
                        </TableCell>
                        <TableCell>
                          <Badge variant={usuario.bodega === 'todas' ? 'default' : 'secondary'}>
                            {usuario.bodega === 'todas' ? 'TODAS' : `Bodega ${usuario.bodega}`}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={usuario.role === 'admin' || usuario.role === 'administrador' ? 'bg-purple-600' : 'bg-blue-600'}>
                            {usuario.role}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            className="mr-2"
                            disabled
                          >
                            Editar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Alert className="bg-green-50 border-green-200">
            <AlertTriangle className="w-4 h-4 text-green-600" />
            <AlertDescription className="text-slate-700">
              <strong>✅ Sistema Operacional:</strong> Puedes crear nuevos usuarios usando el botón "Crear Usuario". 
              Los usuarios creados se guardan en la base de datos y tendrán acceso a la bodega seleccionada.
            </AlertDescription>
          </Alert>
        </>
      )}

      {/* System Tab */}
      {activeAdminTab === 'system' && !userInfo?.configuracion?.ocultar_sistema && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-600" />
                Estado del Sistema
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                    <div>
                      <p className="font-medium text-slate-900">Webhooks Activos</p>
                      <p className="text-sm text-slate-600">Sincronización automática en tiempo real desde Bitua</p>
                    </div>
                  </div>
                  <Badge className="bg-green-600">Operacional</Badge>
                </div>

                <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                    <div>
                      <p className="font-medium text-slate-900">Backend API</p>
                      <p className="text-sm text-slate-600">FastAPI + Supabase PostgreSQL</p>
                    </div>
                  </div>
                  <Badge className="bg-blue-600">Online</Badge>
                </div>

                <div className="flex items-center justify-between p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-purple-500 rounded-full animate-pulse"></div>
                    <div>
                      <p className="font-medium text-slate-900">Sincronización Multi-Bodega</p>
                      <p className="text-sm text-slate-600">{adminData.bodegas.length} ubicaciones conectadas a Bitua API</p>
                    </div>
                  </div>
                  <Badge className="bg-purple-600">Activo</Badge>
                </div>

                <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse"></div>
                    <div>
                      <p className="font-medium text-slate-900">Base de Datos</p>
                      <p className="text-sm text-slate-600">
                        {adminData.totalesGlobales?.total_productos_minimos || 0} productos • {adminData.totalesGlobales?.total_items_inventario || 0} items
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-emerald-600">Conectado</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200">
            <CardContent className="p-6">
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Información del Sistema</h3>
                  <p className="text-sm text-slate-600 mt-1">
                    MD Link Dashboard v2.1 - Sistema de Gestión de Inventario Multi-Bodega (Bitua Live)
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-3 bg-white rounded-lg border border-slate-200">
                    <p className="text-xs text-slate-500">Frontend</p>
                    <p className="font-medium">React 18 + Tailwind CSS</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg border border-slate-200">
                    <p className="text-xs text-slate-500">Backend</p>
                    <p className="font-medium">FastAPI (Python)</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg border border-slate-200">
                    <p className="text-xs text-slate-500">Base de Datos</p>
                    <p className="font-medium">Supabase (PostgreSQL)</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg border border-slate-200">
                    <p className="text-xs text-slate-500">API Externa</p>
                    <p className="font-medium">Bitua RFID Platform</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Modal para crear/editar usuario */}
      {showUserModal && (
        <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingUser ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
              </DialogTitle>
              <DialogDescription>
                Complete la información del usuario para {editingUser ? 'actualizar' : 'crear'} en el sistema
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium">Username *</label>
                <Input
                  value={userForm.username}
                  onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                  placeholder="usuario123"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Contraseña *</label>
                <Input
                  type="password"
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  placeholder="••••••••"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Nombre Completo *</label>
                <Input
                  value={userForm.nombre_completo}
                  onChange={(e) => setUserForm({ ...userForm, nombre_completo: e.target.value })}
                  placeholder="Juan Pérez"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Email</label>
                <Input
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  placeholder="usuario@mdlink.com"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Bodega Asignada</label>
                <select
                  value={userForm.bodega}
                  onChange={(e) => setUserForm({ ...userForm, bodega: e.target.value })}
                  className="w-full p-2 border rounded-lg"
                >
                  {adminData.bodegas.length > 0 ? (
                    adminData.bodegas.map((bodega) => (
                      <option key={bodega.codigo} value={bodega.codigo}>
                        {bodega.nombre}
                      </option>
                    ))
                  ) : (
                    <>
                      <option value="HH">Bodega HH - Anaquel de Heredia</option>
                      <option value="30">Bodega 30 - Hospital Alajuela</option>
                      <option value="16">Bodega 16 - Count Heredia</option>
                      <option value="24">Bodega 24 - H.Calderon</option>
                      <option value="20">Bodega 20 - H.San Juan</option>
                      <option value="23">Bodega 23 - H.Mexico</option>
                      <option value="PT">Hospital de Puntarenas</option>
                    </>
                  )}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Rol</label>
                <select
                  value={userForm.rol}
                  onChange={(e) => setUserForm({ ...userForm, rol: e.target.value })}
                  className="w-full p-2 border rounded-lg"
                >
                  <option value="bodega">Usuario Bodega</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="administrador">Administrador</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowUserModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveUser} className="bg-emerald-600 hover:bg-emerald-700">
                {editingUser ? 'Actualizar' : 'Crear'} Usuario
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// Componente para el modal de productos por categoría
const ProductsByCategoryModal = ({ isOpen, onClose, category, products, loading }) => {
  const getCategoryLabel = (cat) => {
    const labels = {
      'igual_minimo': 'Igual al Mínimo',
      'superior_minimo': 'Superior al Mínimo',
      'inferior_minimo': 'Inferior al Mínimo',
      'en_cero': 'En Cero',
      'sobre_stock': 'Sobre-Stock',
      'productos_minimos_igual_cero': 'Mínimos Igual Cero'
    };
    return labels[cat] || cat;
  };

  const getCategoryColor = (cat) => {
    const colors = {
      'igual_minimo': 'text-blue-600',
      'superior_minimo': 'text-green-600',
      'inferior_minimo': 'text-yellow-600',
      'en_cero': 'text-red-600',
      'sobre_stock': 'text-purple-600',
      'productos_minimos_igual_cero': 'text-gray-600'
    };
    return colors[cat] || 'text-gray-600';
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${getCategoryColor(category)}`}>
            <Package className="w-5 h-5" />
            Productos - {getCategoryLabel(category)}
          </DialogTitle>
          <DialogDescription>
            Lista detallada de productos en esta categoría con SKU, descripción y stock actual.
          </DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="mt-4">
            {products.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No hay productos en esta categoría
              </div>
            ) : (
              <>
                <div className="mb-4 text-sm text-gray-600">
                  Total de productos: {products.length}
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>SKU</TableHead>
                        <TableHead>Descripción</TableHead>
                        <TableHead>Lote</TableHead>
                        <TableHead>Stock Actual</TableHead>
                        <TableHead>Mínimo Establecido</TableHead>
                        <TableHead>Estado</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {products.map((product, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-mono font-medium">
                            {product.sku}
                          </TableCell>
                          <TableCell>
                            {product.description}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {product.lote || 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Badge variant={product.current_stock > 0 ? "default" : "destructive"}>
                              {product.current_stock} unidades
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {product.minimum} unidades
                          </TableCell>
                          <TableCell>
                            <Badge variant={
                              product.current_stock === 0 ? "destructive" :
                              product.current_stock < product.minimum ? "secondary" : "default"
                            }>
                              {product.current_stock === 0 ? "Sin Stock" :
                               product.current_stock < product.minimum ? "Por debajo" : "OK"}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ExpiryRiskSection = ({ riskItems = [], onRefresh }) => {
  const [sortBy, setSortBy] = useState('days_to_expiry');
  const [order, setOrder] = useState('asc');
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setFilteredItems(riskItems);
  }, [riskItems]);

  const fetchRiskItems = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/inventory/risk?sort_by=${sortBy}&order=${order}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFilteredItems(response.data.risk_items || []);
    } catch (error) {
      toast.error("Error al cargar productos en riesgo de vencimiento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRiskItems();
  }, [sortBy, order]);

  const getRiskColor = (riskLevel) => {
    switch (riskLevel) {
      case 'expired': return 'bg-red-900 text-white border-red-950';  // NUEVO: Productos vencidos
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getRiskText = (riskLevel) => {
    switch (riskLevel) {
      case 'expired': return '⛔ VENCIDO';  // NUEVO
      case 'critical': return 'Crítico (≤7 días)';
      case 'high': return 'Alto (≤30 días)';
      case 'medium': return 'Medio (≤60 días)';
      case 'low': return 'Bajo (≤90 días)';
      default: return 'Sin clasificar';
    }
  };

  const summary = filteredItems.reduce((acc, item) => {
    acc[item.risk_level] = (acc[item.risk_level] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-r from-red-50 via-orange-50 to-yellow-50 border-red-200">
        <CardHeader>
          <CardTitle className="text-red-900 flex items-center gap-2 text-xl">
            <Calendar className="w-6 h-6" />
            🚨 Productos en Riesgo de Vencimiento
          </CardTitle>
          <CardDescription className="text-red-700">
            Productos que vencen en los próximos 3 meses. Ordenados desde el mayor riesgo hasta el más lejano.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            {['expired', 'critical', 'high', 'medium', 'low'].map(level => (
              <div key={level} className={`p-4 rounded-lg text-center ${getRiskColor(level)}`}>
                <div className="text-2xl font-bold">{summary[level] || 0}</div>
                <div className="text-sm">{getRiskText(level)}</div>
              </div>
            ))}
          </div>

          {/* Sorting Controls */}
          <div className="flex flex-wrap gap-4 mb-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Ordenar por:</label>
              <select 
                value={sortBy} 
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-1 border rounded-md text-sm"
              >
                <option value="days_to_expiry">Días hasta vencimiento</option>
                <option value="risk_level">Nivel de riesgo</option>
                <option value="expiry_date">Fecha de vencimiento</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Orden:</label>
              <select 
                value={order} 
                onChange={(e) => setOrder(e.target.value)}
                className="px-3 py-1 border rounded-md text-sm"
              >
                <option value="asc">Mayor riesgo primero</option>
                <option value="desc">Menor riesgo primero</option>
              </select>
            </div>
            <Button 
              onClick={fetchRiskItems}
              disabled={loading}
              variant="outline"
              size="sm"
            >
              {loading ? 'Actualizando...' : 'Actualizar'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Products Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-gray-900">
            Lista de Productos ({filteredItems.length} items)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Cargando productos en riesgo...</div>
          ) : filteredItems.length === 0 ? (
            <Alert>
              <Calendar className="h-4 w-4" />
              <AlertDescription>
                ✅ ¡Excelente! No hay productos en riesgo de vencimiento en los próximos 3 meses.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>SKU</TableHead>
                    <TableHead>Descripción</TableHead>
                    <TableHead>Lote</TableHead>
                    <TableHead>Fecha Vencimiento</TableHead>
                    <TableHead>Días Restantes</TableHead>
                    <TableHead>Unidades</TableHead>
                    <TableHead>Nivel de Riesgo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredItems.map((item, index) => (
                    <TableRow key={`${item.epc}-${index}`}>
                      <TableCell className="font-medium">{item.sku}</TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell>{item.batch}</TableCell>
                      <TableCell>{item.expiry_date_formatted}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          item.is_expired ? 'bg-red-900 text-white' :
                          item.days_to_expiry <= 7 ? 'bg-red-100 text-red-800' :
                          item.days_to_expiry <= 30 ? 'bg-orange-100 text-orange-800' :
                          item.days_to_expiry <= 60 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {item.is_expired 
                            ? `⛔ Vencido hace ${Math.abs(item.days_to_expiry)} días` 
                            : `${item.days_to_expiry} días`}
                        </span>
                      </TableCell>
                      <TableCell>{item.units}</TableCell>
                      <TableCell>
                        <Badge className={getRiskColor(item.risk_level)}>
                          {getRiskText(item.risk_level)}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Componente de Reposiciones
const ReposicionesSection = ({ token, userInfo }) => {
  const [reposiciones, setReposiciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroTexto, setFiltroTexto] = useState('');
  
  // Productos críticos
  const [productosCriticos, setProductosCriticos] = useState([]);
  const [loadingCriticos, setLoadingCriticos] = useState(false);
  
  // Estados para el formulario de nueva reposición
  const [showNewReposicionForm, setShowNewReposicionForm] = useState(false);
  const [searchSKU, setSearchSKU] = useState('');
  const [productosDisponibles, setProductosDisponibles] = useState([]);
  const [loadingProductos, setLoadingProductos] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Estados del formulario
  const [formData, setFormData] = useState({
    cantidad: '',
    responsable: '',
    fechaLlegada: '',
    comentarios: '',
    confirmado: false
  });

  useEffect(() => {
    fetchReposiciones();
    fetchProductosCriticos();
  }, [token]);

  const fetchReposiciones = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/data/reposiciones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('✅ Reposiciones fetched:', response.data);
      setReposiciones(response.data.reposiciones || []);
    } catch (error) {
      console.error('❌ Error fetching reposiciones:', error);
      toast.error('Error al cargar el historial de reposiciones');
    } finally {
      setLoading(false);
    }
  };

  // Obtener productos críticos
  const fetchProductosCriticos = async () => {
    try {
      setLoadingCriticos(true);
      const response = await axios.get(`${API}/analytics/critical-items`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filtrar solo productos con stock cero o bajo mínimo
      const criticos = response.data.critical_items.filter(item => 
        item.criticality_level === 'zero_stock' || 
        item.criticality_level === 'below_minimum'
      );
      
      // Ordenar: primero cero stock, luego bajo mínimo
      criticos.sort((a, b) => {
        if (a.criticality_level === 'zero_stock' && b.criticality_level !== 'zero_stock') return -1;
        if (a.criticality_level !== 'zero_stock' && b.criticality_level === 'zero_stock') return 1;
        return 0;
      });
      
      setProductosCriticos(criticos);
      console.log('✅ Productos críticos fetched:', criticos.length);
    } catch (error) {
      console.error('❌ Error fetching productos críticos:', error);
    } finally {
      setLoadingCriticos(false);
    }
  };

  // Buscar productos críticos
  const searchCriticalProducts = async () => {
    if (!searchSKU || searchSKU.length < 2) {
      toast.error('Ingresa al menos 2 caracteres para buscar');
      return;
    }

    try {
      setLoadingProductos(true);
      const response = await axios.get(`${API}/analytics/critical-items`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filtrar por SKU
      const filtered = response.data.critical_items.filter(item => 
        item.sku?.toLowerCase().includes(searchSKU.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchSKU.toLowerCase())
      );
      
      setProductosDisponibles(filtered);
      
      if (filtered.length === 0) {
        toast.info('No se encontraron productos críticos con ese SKU');
      }
    } catch (error) {
      console.error('Error buscando productos:', error);
      toast.error('Error al buscar productos');
    } finally {
      setLoadingProductos(false);
    }
  };

  // Seleccionar producto (desde búsqueda o desde lista de críticos)
  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    const cantidadRecomendada = Math.max(0, (product.minimum_level || 0) - (product.current_stock || 0));
    setFormData(prev => ({
      ...prev,
      cantidad: cantidadRecomendada.toString()
    }));
    setShowNewReposicionForm(true);
    
    // Scroll al formulario
    setTimeout(() => {
      const formElement = document.getElementById('reposicion-form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Registrar reposición
  const handleRegistrarReposicion = async () => {
    if (!selectedProduct) {
      toast.error('Debes seleccionar un producto');
      return;
    }

    if (!formData.confirmado) {
      toast.error('Debes confirmar que los datos son correctos');
      return;
    }

    if (!formData.cantidad || parseInt(formData.cantidad) <= 0) {
      toast.error('La cantidad debe ser mayor a 0');
      return;
    }

    if (!formData.responsable || formData.responsable.trim() === '') {
      toast.error('Debes ingresar el nombre del responsable');
      return;
    }

    try {
      toast.loading('Registrando reposición...', { id: 'reposicion' });
      
      const response = await axios.post(
        `${API}/reposiciones/registrar`,
        {
          sku: selectedProduct.sku,
          producto_nombre: selectedProduct.description,
          cantidad_reponer: parseInt(formData.cantidad),
          responsable_nombre: formData.responsable,
          fecha_estimada_llegada: formData.fechaLlegada || null,
          comentarios: formData.comentarios || null,
          stock_actual: selectedProduct.current_stock,
          minimo_establecido: selectedProduct.minimum_level
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success(`✅ Reposición registrada: ${response.data.data.cantidad_reponer} unidades de ${response.data.data.sku}`, { id: 'reposicion' });
      
      // Reset form
      setShowNewReposicionForm(false);
      setSelectedProduct(null);
      setSearchSKU('');
      setProductosDisponibles([]);
      setFormData({
        cantidad: '',
        responsable: '',
        fechaLlegada: '',
        comentarios: '',
        confirmado: false
      });
      
      // Refresh lists
      fetchReposiciones();
      fetchProductosCriticos();

    } catch (error) {
      console.error('Error registrando reposición:', error);
      toast.error(`Error al registrar reposición: ${error.response?.data?.detail || error.message}`, { id: 'reposicion' });
    }
  };

  // Filtrar reposiciones
  const reposicionesFiltradas = reposiciones.filter(repo => {
    if (!filtroTexto) return true;
    const searchText = filtroTexto.toLowerCase();
    return (
      repo.sku?.toLowerCase().includes(searchText) ||
      repo.producto_nombre?.toLowerCase().includes(searchText) ||
      repo.responsable?.toLowerCase().includes(searchText) ||
      repo.usuario_registro?.toLowerCase().includes(searchText)
    );
  });

  // Formatear fecha
  const formatFecha = (fechaStr) => {
    if (!fechaStr) return 'N/A';
    try {
      const fecha = new Date(fechaStr);
      return fecha.toLocaleDateString('es-CR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return fechaStr;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Package className="w-6 h-6 text-cyan-600" />
            Gestión de Reposiciones
            <Badge className="bg-blue-100 text-blue-800">
              {getBodegaFullName(userInfo?.bodega)}
            </Badge>
          </h2>
          <p className="text-slate-600">
            Registra nuevas reposiciones y consulta el historial completo
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowNewReposicionForm(!showNewReposicionForm)}
            className="bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700"
          >
            <Package className="w-4 h-4 mr-2" />
            {showNewReposicionForm ? 'Cancelar' : 'Nueva Reposición'}
          </Button>
          <Button 
            onClick={fetchReposiciones}
            variant="outline"
            className="border-cyan-500 text-cyan-600 hover:bg-cyan-50"
          >
            <Package className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Productos Críticos que Requieren Reposición */}
      <Card className="bg-gradient-to-br from-red-50 to-orange-50 border-red-200">
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-red-800 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Productos Críticos - Requieren Reposición Urgente
              </CardTitle>
              <CardDescription>
                Productos con stock en cero o bajo el nivel mínimo establecido
              </CardDescription>
            </div>
            <Button 
              onClick={fetchProductosCriticos}
              variant="outline"
              size="sm"
              className="border-red-400 text-red-600 hover:bg-red-50"
            >
              <Package className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingCriticos ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
              <p className="text-slate-600 mt-2">Cargando productos críticos...</p>
            </div>
          ) : productosCriticos.length === 0 ? (
            <div className="text-center py-8">
              <Package className="w-12 h-12 text-green-300 mx-auto mb-3" />
              <p className="text-green-700 font-medium">¡Excelente! No hay productos críticos</p>
              <p className="text-green-600 text-sm">Todos los productos tienen stock suficiente</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                <div className="bg-red-100 p-3 rounded-lg border-l-4 border-red-500">
                  <div className="text-xl font-bold text-red-800">
                    {productosCriticos.filter(p => p.criticality_level === 'zero_stock').length}
                  </div>
                  <div className="text-sm text-red-600">Stock en Cero</div>
                </div>
                <div className="bg-orange-100 p-3 rounded-lg border-l-4 border-orange-500">
                  <div className="text-xl font-bold text-orange-800">
                    {productosCriticos.filter(p => p.criticality_level === 'below_minimum').length}
                  </div>
                  <div className="text-sm text-orange-600">Bajo Mínimo</div>
                </div>
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {productosCriticos.map((product, index) => (
                  <div 
                    key={index}
                    className={`p-4 border rounded-lg transition-all ${
                      product.criticality_level === 'zero_stock' 
                        ? 'bg-red-50 border-red-200' 
                        : 'bg-orange-50 border-orange-200'
                    }`}
                  >
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge 
                            className={`${
                              product.criticality_level === 'zero_stock'
                                ? 'bg-red-600 text-white'
                                : 'bg-orange-600 text-white'
                            }`}
                          >
                            {product.criticality_level === 'zero_stock' ? '🔴 SIN STOCK' : '🟠 BAJO MÍNIMO'}
                          </Badge>
                          <Badge variant="outline" className="font-mono text-xs">
                            {product.sku}
                          </Badge>
                        </div>
                        
                        <div className="font-medium text-gray-800 mb-2">
                          {product.description}
                        </div>
                        
                        <div className="grid grid-cols-3 gap-3 text-sm">
                          <div>
                            <span className="text-gray-600">Stock Actual:</span>
                            <span className={`ml-1 font-bold ${
                              product.current_stock === 0 ? 'text-red-600' : 'text-orange-600'
                            }`}>
                              {product.current_stock}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Mínimo:</span>
                            <span className="ml-1 font-bold text-gray-800">
                              {product.minimum_level}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Necesario:</span>
                            <span className="ml-1 font-bold text-green-600">
                              +{Math.max(0, product.minimum_level - product.current_stock)}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <Button
                        onClick={() => handleSelectProduct(product)}
                        className="bg-green-600 hover:bg-green-700 whitespace-nowrap"
                      >
                        <Package className="w-4 h-4 mr-2" />
                        Registrar Reposición
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Formulario de Nueva Reposición */}
      {showNewReposicionForm && (
        <Card id="reposicion-form" className="bg-gradient-to-br from-green-50 to-teal-50 border-green-200">
          <CardHeader>
            <CardTitle className="text-green-800 flex items-center gap-2">
              <Package className="w-5 h-5" />
              📦 Registrar Nueva Reposición
            </CardTitle>
            <CardDescription>
              Busca el producto crítico y completa los detalles de la reposición
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Paso 1: Buscar Producto */}
            {!selectedProduct && (
              <div className="space-y-4">
                <div className="bg-white p-4 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    🔍 Buscar Producto por SKU o Nombre
                  </label>
                  <div className="flex gap-2">
                    <Input
                      value={searchSKU}
                      onChange={(e) => setSearchSKU(e.target.value)}
                      placeholder="Ej: SKU123 o Catéter..."
                      onKeyPress={(e) => e.key === 'Enter' && searchCriticalProducts()}
                    />
                    <Button 
                      onClick={searchCriticalProducts}
                      disabled={loadingProductos}
                      className="bg-cyan-600 hover:bg-cyan-700"
                    >
                      {loadingProductos ? 'Buscando...' : 'Buscar'}
                    </Button>
                  </div>
                </div>

                {/* Resultados de búsqueda */}
                {productosDisponibles.length > 0 && (
                  <div className="bg-white p-4 rounded-lg">
                    <h4 className="font-medium text-gray-700 mb-3">Productos Encontrados ({productosDisponibles.length})</h4>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {productosDisponibles.map((product, index) => (
                        <div 
                          key={index}
                          onClick={() => handleSelectProduct(product)}
                          className="p-3 border border-gray-200 rounded-lg hover:bg-cyan-50 hover:border-cyan-300 cursor-pointer transition-all"
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="font-medium text-gray-800">{product.sku}</div>
                              <div className="text-sm text-gray-600">{product.description}</div>
                              <div className="flex gap-4 mt-2 text-xs">
                                <span className="text-red-600">Stock: {product.current_stock}</span>
                                <span className="text-orange-600">Mínimo: {product.minimum_level}</span>
                                <span className="text-green-600">Recomendado: +{Math.max(0, product.minimum_level - product.current_stock)}</span>
                              </div>
                            </div>
                            <Badge variant="outline" className="ml-2">Seleccionar</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Paso 2: Formulario de Reposición */}
            {selectedProduct && (
              <div className="space-y-4">
                {/* Información del producto seleccionado */}
                <div className="bg-white p-4 rounded-lg space-y-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium text-gray-800">Producto Seleccionado:</h4>
                      <div className="text-sm text-gray-600 mt-1">{selectedProduct.sku} - {selectedProduct.description}</div>
                      <div className="flex gap-4 mt-2 text-xs">
                        <span className="text-red-600">Stock Actual: {selectedProduct.current_stock}</span>
                        <span className="text-orange-600">Mínimo: {selectedProduct.minimum_level}</span>
                        <span className="text-green-600">Recomendado: +{Math.max(0, selectedProduct.minimum_level - selectedProduct.current_stock)}</span>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSelectedProduct(null)}
                    >
                      Cambiar
                    </Button>
                  </div>
                </div>

                <hr />

                {/* Formulario */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      📝 Cantidad a Reponer <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.cantidad}
                      onChange={(e) => setFormData(prev => ({ ...prev, cantidad: e.target.value }))}
                      placeholder="Ej: 50"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Stock nuevo: {(selectedProduct.current_stock || 0) + parseInt(formData.cantidad || 0)} unidades
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      👤 Responsable <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={formData.responsable}
                      onChange={(e) => setFormData(prev => ({ ...prev, responsable: e.target.value }))}
                      placeholder="Nombre completo"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      📅 Fecha Estimada (opcional)
                    </label>
                    <Input
                      type="date"
                      value={formData.fechaLlegada}
                      onChange={(e) => setFormData(prev => ({ ...prev, fechaLlegada: e.target.value }))}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      📄 Comentarios (opcional)
                    </label>
                    <textarea
                      value={formData.comentarios}
                      onChange={(e) => setFormData(prev => ({ ...prev, comentarios: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      rows="3"
                      placeholder="Información adicional..."
                    />
                  </div>
                </div>

                {/* Confirmación */}
                <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.confirmado}
                      onChange={(e) => setFormData(prev => ({ ...prev, confirmado: e.target.checked }))}
                      className="mt-1 h-4 w-4"
                    />
                    <span className="text-sm text-gray-700">
                      <strong>☑️ Confirmo que los datos son correctos</strong> y autorizo esta reposición.
                    </span>
                  </label>
                </div>

                {/* Botones */}
                <div className="flex justify-end gap-2">
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setShowNewReposicionForm(false);
                      setSelectedProduct(null);
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    onClick={handleRegistrarReposicion}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    ✅ Registrar Reposición
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filtros */}
      <Card className="bg-white/90 backdrop-blur-sm border-slate-200">
        <CardHeader>
          <CardTitle className="text-slate-800 text-lg">Historial de Reposiciones</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <Input
                placeholder="Buscar por SKU, producto, responsable o usuario..."
                value={filtroTexto}
                onChange={(e) => setFiltroTexto(e.target.value)}
                className="w-full"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabla de Reposiciones */}
      <Card className="bg-white/90 backdrop-blur-sm border-slate-200">
        <CardHeader>
          <CardTitle className="text-slate-800 flex items-center gap-2">
            <Package className="w-5 h-5 text-cyan-600" />
            Reposiciones Registradas ({reposicionesFiltradas.length})
          </CardTitle>
          <CardDescription>
            Historial completo de reposiciones de inventario
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto"></div>
              <p className="text-slate-600 mt-4">Cargando historial...</p>
            </div>
          ) : reposicionesFiltradas.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 text-lg font-medium">
                {filtroTexto ? 'No se encontraron reposiciones con ese criterio' : 'No hay reposiciones registradas'}
              </p>
              <p className="text-slate-400 text-sm mt-2">
                {filtroTexto ? 'Intenta con otro término de búsqueda' : 'Las reposiciones aparecerán aquí cuando se registren'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-bold">Fecha</TableHead>
                    <TableHead className="font-bold">SKU</TableHead>
                    <TableHead className="font-bold">Producto</TableHead>
                    <TableHead className="font-bold text-right">Cantidad</TableHead>
                    <TableHead className="font-bold">Stock Anterior</TableHead>
                    <TableHead className="font-bold">Stock Nuevo</TableHead>
                    <TableHead className="font-bold">Mínimo</TableHead>
                    <TableHead className="font-bold">Responsable</TableHead>
                    <TableHead className="font-bold">Registrado Por</TableHead>
                    <TableHead className="font-bold">Comentarios</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reposicionesFiltradas.map((repo, index) => (
                    <TableRow key={repo.id || index} className="hover:bg-slate-50">
                      <TableCell className="font-medium text-slate-700">
                        {formatFecha(repo.fecha_registro)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-mono text-xs">
                          {repo.sku}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <div className="truncate" title={repo.producto_nombre}>
                          {repo.producto_nombre}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge className="bg-green-100 text-green-800 font-bold">
                          +{repo.cantidad_repuesta}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {repo.stock_anterior}
                      </TableCell>
                      <TableCell>
                        <span className={`font-bold ${
                          repo.stock_nuevo >= repo.minimo_establecido 
                            ? 'text-green-600' 
                            : 'text-orange-600'
                        }`}>
                          {repo.stock_nuevo}
                        </span>
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {repo.minimo_establecido}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <span className="w-2 h-2 bg-cyan-500 rounded-full"></span>
                          <span className="font-medium text-cyan-700">
                            {repo.responsable}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600 text-sm">
                        {repo.usuario_registro}
                      </TableCell>
                      <TableCell className="max-w-xs text-sm text-slate-500">
                        {repo.comentarios || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Estadísticas Resumen */}
      {reposiciones.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-cyan-50 to-teal-50 border-cyan-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-cyan-700">
                  {reposiciones.length}
                </div>
                <div className="text-sm text-cyan-600 mt-1">
                  Total Reposiciones
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-700">
                  {reposiciones.reduce((sum, r) => sum + r.cantidad_repuesta, 0)}
                </div>
                <div className="text-sm text-green-600 mt-1">
                  Unidades Repuestas
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-700">
                  {new Set(reposiciones.map(r => r.sku)).size}
                </div>
                <div className="text-sm text-blue-600 mt-1">
                  SKUs Únicos
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-700">
                  {new Set(reposiciones.map(r => r.responsable)).size}
                </div>
                <div className="text-sm text-purple-600 mt-1">
                  Responsables Únicos
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

const UserManagementSection = ({ token, userInfo }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [allBodegasUsers, setAllBodegasUsers] = useState([]);
  const [selectedBodega, setSelectedBodega] = useState(userInfo?.bodega || '16');
  const [showAllBodegas, setShowAllBodegas] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    nombre_completo: '',
    email: '',
    rol: 'usuario',
    departamento: 'General',
    telefono: '',
    notas: ''
  });

  // Determinar si el usuario puede ver todas las bodegas
  const canViewAllBodegas = userInfo?.role === 'admin' || userInfo?.role === 'administrador';

  const verifyAdminPassword = async () => {
    try {
      console.log('🔐 Verifying admin password...');
      const response = await axios.post(`${API}/auth/verify-admin-password`, 
        { password: adminPassword },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      console.log('🔐 Admin password response:', response.data);

      if (response.data.access_granted) {
        console.log('✅ Access granted, updating state...');
        setIsAuthenticated(true);
        setShowPasswordForm(false);
        console.log('🔄 State updated, fetching users...');
        await fetchUsers();
        toast.success('Acceso autorizado a gestión de usuarios');
      } else {
        console.log('❌ Access denied');
        toast.error('Contraseña incorrecta');
      }
    } catch (error) {
      console.error('❌ Error verifying password:', error);
      toast.error('Error verificando contraseña');
    }
  };

  const fetchUsers = async (bodegaNum = selectedBodega) => {
    console.log(`📥 Fetching users for bodega ${bodegaNum}...`);
    setLoading(true);
    try {
      if (showAllBodegas && canViewAllBodegas) {
        // Fetch users from all bodegas
        const bodegas = ['16', '20', '23', '24', '30', '94', '214'];
        const allUsers = [];
        
        for (const bodega of bodegas) {
          try {
            const response = await axios.get(`${API}/users/bodega${bodega}/all`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const bodegaUsers = response.data.users.map(user => ({
              ...user,
              bodega: bodega
            }));
            allUsers.push(...bodegaUsers);
          } catch (error) {
            console.warn(`⚠️ Error fetching users from bodega ${bodega}:`, error);
          }
        }
        
        setUsers(allUsers);
        console.log(`📥 All users fetched: ${allUsers.length} users from all bodegas`);
      } else {
        // Fetch users from specific bodega
        const response = await axios.get(`${API}/users/bodega${bodegaNum}/all`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log(`📥 Users fetched from bodega ${bodegaNum}:`, response.data.users.length, 'users');
        setUsers(response.data.users);
      }
    } catch (error) {
      console.error('❌ Error fetching users:', error);
      toast.error('Error cargando usuarios');
    } finally {
      setLoading(false);
    }
  };

  const createUser = async () => {
    try {
      const targetBodega = showAllBodegas ? selectedBodega : userInfo?.bodega || '16';
      
      await axios.post(`${API}/users/bodega${targetBodega}/create`, newUser, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Usuario creado exitosamente en Bodega ${targetBodega}`);
      setNewUser({
        username: '',
        password: '',
        nombre_completo: '',
        email: '',
        rol: 'usuario',
        departamento: 'General',
        telefono: '',
        notas: ''
      });
      setShowCreateForm(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creando usuario');
    }
  };

  const updateUser = async (userId, userData, userBodega) => {
    try {
      const targetBodega = userBodega || selectedBodega;
      
      await axios.put(`${API}/users/bodega${targetBodega}/${userId}`, userData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Usuario actualizado exitosamente');
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error('Error actualizando usuario');
    }
  };

  const toggleUserStatus = async (userId, currentStatus, userBodega) => {
    try {
      const targetBodega = userBodega || selectedBodega;
      
      await axios.put(`${API}/users/bodega${targetBodega}/${userId}`, 
        { activo: !currentStatus },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success(`Usuario ${!currentStatus ? 'activado' : 'desactivado'} exitosamente`);
      fetchUsers();
    } catch (error) {
      toast.error('Error cambiando estado del usuario');
    }
  };

  const changePassword = async (userId, newPassword, userBodega) => {
    try {
      const targetBodega = userBodega || selectedBodega;
      
      await axios.post(`${API}/users/bodega${targetBodega}/${userId}/change-password`, 
        { new_password: newPassword },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Contraseña actualizada exitosamente');
    } catch (error) {
      toast.error('Error cambiando contraseña');
    }
  };

  console.log('🎭 UserManagementSection render - showPasswordForm:', showPasswordForm, 'isAuthenticated:', isAuthenticated);

  if (showPasswordForm) {
    return (
      <div className="max-w-md mx-auto">
        <Card className="bg-white/90 backdrop-blur-sm border-slate-200">
          <CardHeader>
            <CardTitle className="text-cyan-900 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Gestión de Usuarios
            </CardTitle>
            <CardDescription>
              Ingresa la contraseña de administración para acceder
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Input
                type="password"
                placeholder="Contraseña de administración"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    verifyAdminPassword();
                  }
                }}
              />
            </div>
            <Button 
              onClick={verifyAdminPassword} 
              className="w-full bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700"
            >
              Acceder
            </Button>
            <div className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
              <p><strong>Contraseña:</strong> GestionB16_Admin2025!</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Users className="w-6 h-6 text-cyan-600" />
            Gestión de Usuarios
            {showAllBodegas ? (
              <Badge className="bg-purple-100 text-purple-800">Todas las Bodegas</Badge>
            ) : (
              <Badge className="bg-blue-100 text-blue-800">Bodega {selectedBodega}</Badge>
            )}
          </h2>
          <p className="text-slate-600">
            {showAllBodegas 
              ? "Sistema completo de usuarios para todas las bodegas" 
              : `Sistema de usuarios para Bodega ${selectedBodega}`
            }
          </p>
        </div>

        <div className="flex gap-2">
          {canViewAllBodegas && (
            <div className="flex items-center gap-2">
              <select 
                className="p-2 border border-slate-300 rounded-md text-sm"
                value={showAllBodegas ? 'all' : selectedBodega}
                onChange={(e) => {
                  if (e.target.value === 'all') {
                    setShowAllBodegas(true);
                  } else {
                    setShowAllBodegas(false);
                    setSelectedBodega(e.target.value);
                  }
                  fetchUsers(e.target.value === 'all' ? '16' : e.target.value);
                }}
              >
                <option value="all">🌐 Todas las Bodegas</option>
                <option value="16">🏢 Bodega 16</option>
                <option value="20">🏢 Bodega 20</option>
                <option value="23">🏢 Bodega 23</option>
                <option value="24">🏢 Bodega 24</option>
                <option value="30">🏢 Bodega 30</option>
                <option value="94">🏢 Bodega 94</option>
                <option value="214">🏢 Bodega 214</option>
              </select>
            </div>
          )}
          
          <Button 
            onClick={() => setShowCreateForm(true)}
            className="bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700"
          >
            <Users className="w-4 h-4 mr-2" />
            Crear Usuario
          </Button>
        </div>
      </div>

      {/* Formulario de creación */}
      {showCreateForm && (
        <Card className="bg-white/90 backdrop-blur-sm border-slate-200">
          <CardHeader>
            <CardTitle className="text-green-900">Crear Nuevo Usuario</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Username</label>
                <Input
                  value={newUser.username}
                  onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                  placeholder="username_b16"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Contraseña</label>
                <Input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  placeholder="Mínimo 6 caracteres"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nombre Completo</label>
                <Input
                  value={newUser.nombre_completo}
                  onChange={(e) => setNewUser({...newUser, nombre_completo: e.target.value})}
                  placeholder="Nombre y apellidos"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <Input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  placeholder="email@hospital.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Rol</label>
                <select 
                  className="w-full p-2 border border-slate-300 rounded-md"
                  value={newUser.rol}
                  onChange={(e) => setNewUser({...newUser, rol: e.target.value})}
                >
                  <option value="usuario">Usuario</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="administrador">Administrador</option>
                  <option value="solo_lectura">Solo Lectura</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Departamento</label>
                <Input
                  value={newUser.departamento}
                  onChange={(e) => setNewUser({...newUser, departamento: e.target.value})}
                  placeholder="Enfermería, Medicina, etc."
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={createUser}
                className="bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700"
              >
                Crear Usuario
              </Button>
              <Button 
                onClick={() => setShowCreateForm(false)}
                variant="outline"
              >
                Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lista de usuarios */}
      <Card className="bg-white/90 backdrop-blur-sm border-slate-200">
        <CardHeader>
          <CardTitle className="text-slate-800">
            Usuarios de Bodega 16 ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600 mx-auto"></div>
              <p className="text-slate-600 mt-2">Cargando usuarios...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Username</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Rol</TableHead>
                    <TableHead>Departamento</TableHead>
                    {showAllBodegas && <TableHead>Bodega</TableHead>}
                    <TableHead>Estado</TableHead>
                    <TableHead>Último Acceso</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={`${user.bodega || selectedBodega}-${user.id}`}>
                      <TableCell className="font-medium">{user.username}</TableCell>
                      <TableCell>{user.nombre_completo}</TableCell>
                      <TableCell>{user.email || 'N/A'}</TableCell>
                      <TableCell>
                        <Badge className={
                          user.rol === 'administrador' ? 'bg-red-100 text-red-800' :
                          user.rol === 'supervisor' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }>
                          {user.rol}
                        </Badge>
                      </TableCell>
                      <TableCell>{user.departamento}</TableCell>
                      {showAllBodegas && (
                        <TableCell>
                          <Badge className="bg-cyan-100 text-cyan-800">
                            {user.bodega || selectedBodega}
                          </Badge>
                        </TableCell>
                      )}
                      <TableCell>
                        <Badge className={user.activo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {user.activo ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {user.ultimo_acceso ? new Date(user.ultimo_acceso).toLocaleDateString() : 'Nunca'}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingUser(user)}
                          >
                            Editar
                          </Button>
                          <Button
                            size="sm"
                            variant={user.activo ? "destructive" : "default"}
                            onClick={() => toggleUserStatus(user.id, user.activo, user.bodega)}
                          >
                            {user.activo ? 'Desactivar' : 'Activar'}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de edición */}
      {editingUser && (
        <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Editar Usuario: {editingUser.username}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nombre Completo</label>
                <Input
                  value={editingUser.nombre_completo}
                  onChange={(e) => setEditingUser({...editingUser, nombre_completo: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <Input
                  value={editingUser.email || ''}
                  onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Rol</label>
                <select 
                  className="w-full p-2 border border-slate-300 rounded-md"
                  value={editingUser.rol}
                  onChange={(e) => setEditingUser({...editingUser, rol: e.target.value})}
                >
                  <option value="usuario">Usuario</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="administrador">Administrador</option>
                  <option value="solo_lectura">Solo Lectura</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Departamento</label>
                <Input
                  value={editingUser.departamento}
                  onChange={(e) => setEditingUser({...editingUser, departamento: e.target.value})}
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={() => updateUser(editingUser.id, {
                    nombre_completo: editingUser.nombre_completo,
                    email: editingUser.email,
                    rol: editingUser.rol,
                    departamento: editingUser.departamento
                  }, editingUser.bodega)}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                >
                  Guardar Cambios
                </Button>
                <Button 
                  onClick={() => {
                    const newPassword = prompt('Nueva contraseña (mínimo 6 caracteres):');
                    if (newPassword && newPassword.length >= 6) {
                      changePassword(editingUser.id, newPassword, editingUser.bodega);
                    }
                  }}
                  variant="outline"
                >
                  Cambiar Contraseña
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

const Dashboard = ({ onLogout, userInfo, token }) => {
  const [stats, setStats] = useState(null);
  const [riskItems, setRiskItems] = useState([]);
  const [detailedItems, setDetailedItems] = useState([]);
  const [minimosAnalysis, setMinimosAnalysis] = useState(null);
  const [consumptionAnalysis, setConsumptionAnalysis] = useState(null);
  const [consumptionTimeFilter, setConsumptionTimeFilter] = useState('month'); // Default: last month
  const [criticalItems, setCriticalItems] = useState(null);
  const [topSkus, setTopSkus] = useState(null);
  const [replenishmentData, setReplenishmentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  
  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    // Si es admin, iniciar en 'admin', sino verificar si es farmacia o dashboard normal
    const savedUserInfo = localStorage.getItem('userInfo');
    if (savedUserInfo) {
      const info = JSON.parse(savedUserInfo);
      if (info.role === 'admin' || info.role === 'administrador') {
        return 'admin';
      }
      // Si tiene ocultar_reposiciones, es usuario de farmacia
      if (info.configuracion?.ocultar_reposiciones) {
        return 'farmacia';
      }
    }
    return 'dashboard';
  });
  const [isMobile, setIsMobile] = useState(false);
  const [farmaciaActiveFilter, setFarmaciaActiveFilter] = useState(null); // Filtro activo desde el sidebar de farmacia
  
  // 🏢 MODO GESTIÓN DE BODEGA - Para que el admin pueda gestionar cualquier bodega
  const [managingBodega, setManagingBodega] = useState(null); // null = modo admin normal, 'HH' = gestionando HH
  const [availableBodegas, setAvailableBodegas] = useState([]); // Lista de bodegas disponibles para el admin
  
  // Responsive sidebar control
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarOpen(false);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // State para el modal de productos por categoría
  const [categoryModal, setCategoryModal] = useState({
    isOpen: false,
    category: '',
    products: [],
    loading: false
  });

  // Password confirmation modal state
  const [passwordModal, setPasswordModal] = useState({
    isOpen: false,
    action: '',
    title: '',
    description: '',
    password: '',
    onConfirm: null
  });

  // State para el modal de vencimientos (clickeable desde dashboard)
  const [showExpiryModalDashboard, setShowExpiryModalDashboard] = useState(false);

  const fetchDashboardData = async (isManual = false) => {
    if (isManual) {
      setIsRefreshing(true);
    }
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Construir parámetros para consumption con filtro de tiempo
      const consumptionParams = consumptionTimeFilter !== 'all' 
        ? { params: { time_filter: consumptionTimeFilter } }
        : {};
      
      // 🏢 Si el admin está gestionando una bodega específica, agregar filtro
      const bodegaParam = managingBodega ? `?bodega_filter=${managingBodega}` : '';
      const bodegaParamAmp = managingBodega ? `&bodega_filter=${managingBodega}` : '';

      // Usar Promise.allSettled para que un error no rompa todo
      const results = await Promise.allSettled([
        axios.get(`${API}/dashboard/stats${bodegaParam}`, { headers }),
        axios.get(`${API}/inventory/risk${bodegaParam}`, { headers }),
        axios.get(`${API}/inventory/detailed${bodegaParam}`, { headers }),
        axios.get(`${API}/analytics/minimos${bodegaParam}`, { headers }),
        axios.get(`${API}/analytics/consumption${managingBodega ? `?bodega_filter=${managingBodega}${consumptionTimeFilter !== 'all' ? `&time_filter=${consumptionTimeFilter}` : ''}` : (consumptionTimeFilter !== 'all' ? `?time_filter=${consumptionTimeFilter}` : '')}`, { headers }),
        axios.get(`${API}/analytics/critical-items${bodegaParam}`, { headers }),
        axios.get(`${API}/analytics/top-skus${bodegaParam}`, { headers }),
        axios.get(`${API}/analytics/replenishment-needs${bodegaParam}`, { headers })
      ]);

      // Extraer datos de cada resultado, usando valores por defecto si falló
      const [statsResult, riskResult, detailedResult, minimosResult, consumptionResult, criticalResult, topSkusResult, replenishmentResult] = results;

      if (statsResult.status === 'fulfilled') setStats(statsResult.value.data);
      if (riskResult.status === 'fulfilled') setRiskItems(riskResult.value.data.risk_items || []);
      if (detailedResult.status === 'fulfilled') setDetailedItems(detailedResult.value.data.detailed_inventory || []);
      if (minimosResult.status === 'fulfilled') setMinimosAnalysis(minimosResult.value.data);
      if (consumptionResult.status === 'fulfilled') setConsumptionAnalysis(consumptionResult.value.data);
      if (criticalResult.status === 'fulfilled') setCriticalItems(criticalResult.value.data);
      if (topSkusResult.status === 'fulfilled') setTopSkus(topSkusResult.value.data);
      if (replenishmentResult.status === 'fulfilled') setReplenishmentData(replenishmentResult.value.data);
      
      setLastUpdate(new Date());
      
      // Contar errores
      const errors = results.filter(r => r.status === 'rejected');
      if (errors.length > 0) {
        console.warn(`⚠️ ${errors.length} endpoints fallaron:`, errors.map(e => e.reason?.message));
      }
      
      if (isManual) {
        if (errors.length > 0) {
          toast.warning(`Dashboard actualizado con ${errors.length} errores`);
        } else {
          toast.success("Dashboard actualizado correctamente");
        }
      }
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        onLogout();
      } else {
        console.error('Error general:', error);
        toast.error("Error al cargar datos del dashboard");
      }
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  // Auto-refresh cada 5 minutos (solo cuando la pestaña está activa)
  useEffect(() => {
    // Si es admin y no está gestionando una bodega, no cargar datos de dashboard
    const isAdmin = userInfo?.role === 'admin' || userInfo?.role === 'administrador';
    if (isAdmin && !managingBodega) {
      setLoading(false);  // Quitar loading para mostrar panel de admin directamente
      return;
    }
    
    fetchDashboardData();
    
    const intervalId = setInterval(() => {
      // Solo auto-refrescar si la pestaña está visible y no somos admin sin bodega gestionada
      const isAdmin = userInfo?.role === 'admin' || userInfo?.role === 'administrador';
      if (!document.hidden && !(isAdmin && !managingBodega)) {
        fetchDashboardData();
      }
    }, 5 * 60 * 1000); // 5 minutos

    return () => clearInterval(intervalId);
  }, [userInfo, managingBodega]);

  // Recargar cuando cambie el filtro de consumos
  useEffect(() => {
    // No cargar para admin sin bodega gestionada
    const isAdmin = userInfo?.role === 'admin' || userInfo?.role === 'administrador';
    if (isAdmin && !managingBodega) {
      return;
    }
    if (consumptionTimeFilter) {
      fetchDashboardData();
    }
  }, [consumptionTimeFilter]);
  
  // 🏢 Recargar datos cuando el admin cambie de bodega gestionada
  useEffect(() => {
    if (userInfo?.role === 'admin' || userInfo?.role === 'administrador') {
      console.log('🏢 Admin managingBodega changed to:', managingBodega);
      if (managingBodega) {
        setLoading(true); // Loading al cambiar bodega
        console.log('🏢 Calling fetchDashboardData with filter:', managingBodega);
        fetchDashboardData();
      }
    }
  }, [managingBodega]);

  // 🏢 Cargar lista de bodegas disponibles para el admin
  useEffect(() => {
    const fetchBodegas = async () => {
      if (userInfo?.role === 'admin' || userInfo?.role === 'administrador') {
        try {
          const response = await axios.get(`${API}/bodegas`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data.bodegas) {
            setAvailableBodegas(response.data.bodegas);
          }
        } catch (error) {
          console.error('Error cargando bodegas:', error);
        }
      }
    };
    fetchBodegas();
  }, [userInfo, token]);

  // 🏢 Función para que el admin entre a gestionar una bodega específica
  const handleManageBodega = (bodegaCodigo) => {
    setLoading(true); // Mostrar loading inmediatamente
    setManagingBodega(bodegaCodigo);
    setActiveTab('dashboard');
    toast.success(`Cargando: ${availableBodegas.find(b => b.codigo === bodegaCodigo)?.nombre || bodegaCodigo}`);
  };

  // 🏢 Función para que el admin vuelva a la vista de administración
  const handleExitManageBodega = () => {
    setManagingBodega(null);
    setActiveTab('admin');
  };
  
  // Manual refresh function
  const handleManualRefresh = () => {
    fetchDashboardData(true);
  };

  const handleUploadSuccess = () => {
    fetchDashboardData();
    toast.success("Datos actualizados exitosamente");
  };

  // Password confirmation functions
  const showPasswordModal = (action, title, description, onConfirm) => {
    setPasswordModal({
      isOpen: true,
      action,
      title,
      description,
      password: '',
      onConfirm
    });
  };

  const handlePasswordSubmit = () => {
    const correctPassword = 'borradototalmeditek25';
    
    if (passwordModal.password === correctPassword) {
      setPasswordModal({ isOpen: false, action: '', title: '', description: '', password: '', onConfirm: null });
      if (passwordModal.onConfirm) {
        passwordModal.onConfirm();
      }
    } else {
      toast.error("❌ Contraseña incorrecta");
      setPasswordModal(prev => ({ ...prev, password: '' }));
    }
  };

  const handlePasswordCancel = () => {
    setPasswordModal({ isOpen: false, action: '', title: '', description: '', password: '', onConfirm: null });
  };

  const fetchProductsByCategory = async (category) => {
    setCategoryModal(prev => ({ ...prev, loading: true }));
    
    try {
      const token = localStorage.getItem('token');
      const bodegaParam = managingBodega ? `?bodega_filter=${managingBodega}` : '';
      const response = await axios.get(`${API}/dashboard/products-by-category/${category}${bodegaParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCategoryModal({
        isOpen: true,
        category: category,
        products: response.data.products,
        loading: false
      });
    } catch (error) {
      console.error('Error fetching products by category:', error);
      toast.error("Error al cargar productos de la categoría");
      setCategoryModal(prev => ({ ...prev, loading: false }));
    }
  };

  const executeDeleteAllData = async () => {
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/data/clear-all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Clear data response:', response.data);
      toast.success("✅ Todos los datos han sido eliminados exitosamente");
      
      // Reset all state to empty
      setStats(null);
      setRiskItems([]);
      setDetailedItems([]);
      setMinimosAnalysis(null);
      setConsumptionAnalysis(null);
      setCriticalItems(null);
      
      // Fetch fresh data (should be empty now)
      setTimeout(() => {
        fetchDashboardData();
      }, 1000);
      
    } catch (error) {
      console.error('Error clearing data:', error);
      toast.error(`❌ Error al eliminar los datos: ${error.response?.data?.detail || error.message}`);
    }
  };

  const executeDeleteInventory = async () => {
    console.log("🗑️ Executing delete inventory...");
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.delete(`${API}/data/clear-inventory`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const details = response.data;
      console.log('✅ Delete inventory response:', details);
      
      const deletedCount = details.records_deleted || 0;
      
      toast.success(`✅ ${deletedCount} registros de inventario eliminados exitosamente`, { id: "clear-inventory" });
      
      // Reload data after deletion
      await fetchDashboardData();
      
    } catch (error) {
      console.error('❌ Error deleting inventory:', error);
      
      let errorMessage = "❌ Error al eliminar el inventario";
      
      if (error.response) {
        console.error('Error response:', error.response.data);
        errorMessage = `❌ Error al eliminar el inventario: ${error.response?.data?.detail || error.message}`;
      } else if (error.request) {
        console.error('Error request:', error.request);
        errorMessage = "❌ Error de conexión al servidor";
      }
      
      toast.error(errorMessage, { id: "clear-inventory" });
    }
  };

  const executeDeleteConsumptions = async () => {
    
    console.log('🗑️ Starting clear consumptions process...');
    console.log('API URL:', `${API}/data/clear-consumptions`);
    
    try {
      const token = localStorage.getItem('token');
      console.log('Token found:', token ? 'YES' : 'NO');
      
      if (!token) {
        toast.error("❌ No hay token de autenticación. Por favor, inicie sesión nuevamente.");
        return;
      }
      
      toast.loading("🗑️ Eliminando datos de consumos...", { id: "clear-consumptions" });
      
      const response = await axios.delete(`${API}/data/clear-consumptions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('✅ Clear consumptions response:', response.data);
      const details = response.data.details || {};
      const deletedCount = details.records_deleted || 0;
      
      toast.success(`✅ ${deletedCount} registros de consumos eliminados exitosamente`, { id: "clear-consumptions" });
      
      // Refresh dashboard to show zeros in consumption-related metrics
      console.log('🔄 Refreshing dashboard data...');
      await fetchDashboardData();
      console.log('✅ Dashboard refreshed');
      
    } catch (error) {
      console.error('❌ Error clearing consumptions:', error);
      
      let errorMessage = "❌ Error al eliminar los datos de consumos";
      
      if (error.response) {
        console.error('Error response:', error.response.data);
        console.error('Error status:', error.response.status);
        
        if (error.response.status === 401) {
          errorMessage = "❌ Sesión expirada. Por favor, inicie sesión nuevamente.";
        } else if (error.response.data?.detail) {
          errorMessage = `❌ Error: ${error.response.data.detail}`;
        }
      } else if (error.request) {
        console.error('Error request:', error.request);
        errorMessage = "❌ Error de conexión al servidor";
      }
      
      toast.error(errorMessage, { id: "clear-consumptions" });
    }
  };

  const handleClearConsumptions = () => {
    showPasswordModal(
      'delete_consumptions',
      '🗑️ Eliminar Solo Consumos',
      '⚠️ Esta acción eliminará SOLO los datos de consumos:\n\n• Todos los consumos ❌\n\nPERO CONSERVARÁ:\n• Los mínimos establecidos ✅\n• El inventario actual ✅\n\nEsta acción NO se puede deshacer.\n\nIngrese la contraseña para continuar:',
      executeDeleteConsumptions
    );
  };

  const handleClearInventory = () => {
    showPasswordModal(
      'delete_inventory',
      '🗑️ Eliminar Inventario',
      '⚠️ Esta acción eliminará SOLO el inventario:\n\n• Todo el inventario ❌\n\nPERO CONSERVARÁ:\n• Los mínimos establecidos ✅\n• Los consumos registrados ✅\n\nEsta acción NO se puede deshacer.\n\nIngrese la contraseña para continuar:',
      executeDeleteInventory
    );
  };

  const handleClearAllData = () => {
    showPasswordModal(
      'delete_all',
      '🗑️ Eliminar Todos los Datos',
      '⚠️ ADVERTENCIA: Esta acción eliminará TODOS los datos:\n\n• Todos los mínimos\n• Todo el inventario\n• Todos los consumos\n\nEsta acción NO se puede deshacer.\n\nIngrese la contraseña para continuar:',
      executeDeleteAllData
    );
  };

  const handleCheckCriticalStock = async () => {
    console.log('🔍 Starting critical stock check...');
    
    // Determinar la bodega correcta:
    // 1. Si es admin gestionando una bodega específica, usar managingBodega
    // 2. Si es usuario de bodega, usar userInfo.bodega
    // 3. Si es admin sin gestionar, usar la bodega que tenga seleccionada o default
    let targetBodega = null;
    
    if (managingBodega) {
      // Admin está gestionando una bodega específica
      targetBodega = managingBodega;
    } else if (userInfo?.bodega && userInfo.bodega !== 'ALL' && userInfo.bodega !== 'all') {
      // Usuario de bodega específica
      targetBodega = userInfo.bodega;
    }
    
    console.log('📍 Bodega objetivo:', targetBodega);
    console.log('👤 Usuario:', userInfo?.username, 'Rol:', userInfo?.role, 'Bodega asignada:', userInfo?.bodega);
    
    try {
      const token = localStorage.getItem('token');
      console.log('Token found:', token ? 'YES' : 'NO');
      
      if (!token) {
        toast.error("❌ No hay token de autenticación. Por favor, inicie sesión nuevamente.");
        return;
      }
      
      // Construir URL con bodega_filter
      const bodegaParam = targetBodega ? `?bodega_filter=${targetBodega}` : '';
      const bodegaName = targetBodega ? ` para Bodega ${targetBodega}` : '';
      
      toast.loading(`🔍 Verificando stock crítico${bodegaName} y enviando alertas...`, { id: "check-critical" });
      
      const response = await axios.post(`${API}/alerts/check-critical-stock${bodegaParam}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('✅ Critical stock check response:', response.data);
      
      const data = response.data;
      
      const totalCritical = data.total_critical_items || 0;
      const zeroStock = data.products_with_zero_stock_count || 0;
      const belowMin = data.critical_products_count || 0;
      const nearExpiry = data.products_near_expiry_count || 0;
      const alreadyExpired = data.products_already_expired_count || 0;  // NUEVO
      
      if (totalCritical > 0) {
        const bodegaDisplay = targetBodega || 'todas';
        let message = `🚨 Bodega ${bodegaDisplay}: ${totalCritical} productos críticos:\n`;
        if (alreadyExpired > 0) message += `• ⛔ ${alreadyExpired} productos YA VENCIDOS\n`;
        if (zeroStock > 0) message += `• ${zeroStock} con stock en CERO\n`;
        if (belowMin > 0) message += `• ${belowMin} bajo mínimo\n`;
        if (nearExpiry > 0) message += `• ${nearExpiry} próximos a vencer (90 días)\n`;
        message += `${data.email_sent ? '\n✉️ Alerta enviada por correo.' : '\n⚠️ No se pudo enviar correo.'}`;
        
        toast.success(message, { id: "check-critical", duration: 8000 });
        
        // Refresh dashboard to show updated critical status
        console.log('🔄 Refreshing dashboard after critical check...');
        await fetchDashboardData();
      } else {
        toast.success(`✅ Bodega ${targetBodega || 'todas'}: No se encontraron productos críticos. Todo está bien.`, { id: "check-critical" });
      }
      
    } catch (error) {
      console.error('❌ Error checking critical stock:', error);
      
      let errorMessage = "❌ Error al verificar stock crítico";
      
      if (error.response) {
        console.error('Error response:', error.response.data);
        
        if (error.response.status === 401) {
          errorMessage = "❌ Sesión expirada. Por favor, inicie sesión nuevamente.";
        } else if (error.response.data?.detail) {
          errorMessage = `❌ Error: ${error.response.data.detail}`;
        }
      } else if (error.request) {
        errorMessage = "❌ Error de conexión al servidor";
      }
      
      toast.error(errorMessage, { id: "check-critical" });
    }
  };

  const handleSyncBitua = async () => {
    console.log('🔄 Starting Bitua synchronization...');
    
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error("❌ No hay token de autenticación. Por favor, inicie sesión nuevamente.");
        return;
      }
      
      toast.loading("🔄 Sincronizando todas las bodegas con Bitua...", { id: "sync-bitua" });
      
      // Usar el nuevo endpoint mejorado
      const response = await axios.post(`${API}/bitua/sync-all-auto`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('✅ Bitua sync response:', response.data);
      
      const data = response.data;
      
      if (data.success) {
        const summary = data.summary;
        let message = `✅ Sincronización completada:\n`;
        message += `• ${summary.successful}/${summary.total_locations} bodegas sincronizadas\n`;
        message += `• Items antes: ${summary.total_items_before}\n`;
        message += `• Items después: ${summary.total_items_after}\n`;
        message += `• Cambio: ${summary.items_change > 0 ? '+' : ''}${summary.items_change} items\n`;
        if (summary.discrepancies_fixed > 0) {
          message += `• ✅ ${summary.discrepancies_fixed} discrepancias corregidas\n`;
        }
        
        toast.success(message, { id: "sync-bitua", duration: 10000 });
        
        // Mostrar detalles por bodega
        if (data.details && data.details.length > 0) {
          data.details.forEach(detail => {
            if (detail.status === 'success' || detail.status === 'partial') {
              const bodegaMsg = `${detail.bodega}: ${detail.before} → ${detail.after} (Bitua: ${detail.bitua_total})`;
              console.log(`📊 ${bodegaMsg}`);
            }
          });
        }
        
        // Refresh dashboard to show updated data
        console.log('🔄 Refreshing dashboard after Bitua sync...');
        await fetchDashboardData();
      } else {
        toast.warning(`⚠️ Sincronización completada con advertencias: ${data.message || 'Ver logs para más detalles'}`, { id: "sync-bitua" });
      }
      
    } catch (error) {
      console.error('❌ Error syncing with Bitua:', error);
      
      let errorMessage = "❌ Error al sincronizar con Bitua";
      
      if (error.response) {
        console.error('Error response:', error.response.data);
        
        if (error.response.status === 401) {
          errorMessage = "❌ Sesión expirada. Por favor, inicie sesión nuevamente.";
        } else if (error.response.status === 403) {
          errorMessage = "❌ No tienes permisos para realizar esta sincronización.";
        } else if (error.response.data?.detail) {
          errorMessage = `❌ Error: ${error.response.data.detail}`;
        }
      } else if (error.request) {
        errorMessage = "❌ Error de conexión al servidor";
      }
      
      toast.error(errorMessage, { id: "sync-bitua" });
    }
  };

  if (loading) {
    const bodegaName = managingBodega 
      ? (availableBodegas.find(b => b.codigo === managingBodega)?.nombre || `Bodega ${managingBodega}`)
      : null;
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-slate-200 flex items-center justify-center">
        <div className="text-center">
          <div className="relative mx-auto mb-6 w-20 h-20">
            <div className="w-20 h-20 border-4 border-blue-200 rounded-full animate-spin border-t-blue-600"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <Package className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <p className="text-xl font-semibold text-blue-900">
            {bodegaName ? `Cargando datos de ${bodegaName}` : 'Cargando dashboard...'}
          </p>
          <p className="mt-2 text-sm text-slate-500">
            Consultando inventario en tiempo real
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 overflow-hidden">
      {/* Sidebar */}
      <Sidebar 
        isOpen={sidebarOpen}
        setIsOpen={setSidebarOpen}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        isMobile={isMobile}
        userRole={userInfo.role}
        hideReposiciones={userInfo?.configuracion?.ocultar_reposiciones || false}
        onFarmaciaFilterChange={setFarmaciaActiveFilter}
      />
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* 🏢 Banner de Gestión de Bodega - Solo visible cuando el admin gestiona una bodega específica */}
        {managingBodega && (userInfo?.role === 'admin' || userInfo?.role === 'administrador') && (
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center font-bold text-white text-sm">
                {managingBodega}
              </div>
              <div>
                <p className="text-white font-medium text-sm">
                  Gestionando: {availableBodegas.find(b => b.codigo === managingBodega)?.nombre || `Bodega ${managingBodega}`}
                </p>
                <p className="text-emerald-100 text-xs">Vista completa como usuario de esta bodega</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Dropdown para cambiar de bodega rápidamente */}
              <select
                value={managingBodega}
                onChange={(e) => handleManageBodega(e.target.value)}
                className="bg-white/20 text-white text-sm rounded-lg px-3 py-1.5 border border-white/30 focus:outline-none focus:ring-2 focus:ring-white/50"
              >
                {availableBodegas.map(b => (
                  <option key={b.codigo} value={b.codigo} className="text-gray-900">
                    {b.nombre}
                  </option>
                ))}
              </select>
              <Button
                onClick={handleExitManageBodega}
                variant="outline"
                size="sm"
                className="bg-white/10 border-white/30 text-white hover:bg-white/20"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Volver a Admin
              </Button>
            </div>
          </div>
        )}
        
        {/* Dashboard Header */}
        <div className="bg-gradient-to-r from-blue-800 via-blue-700 to-indigo-800 shadow-2xl border-b border-blue-600/20 flex-shrink-0">
          <div className="px-4 md:px-6 py-3 md:py-4 flex justify-between items-center gap-2 md:gap-4">
          <div className="flex items-center gap-2 md:gap-4">
            {/* Hamburger Menu for Mobile */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <Menu className="w-6 h-6 text-white" />
            </button>
            
            <div className="hidden md:block p-2 bg-white/20 rounded-xl shadow-lg">
              <BarChart3 className="w-6 h-6 md:w-8 md:h-8 text-white" />
            </div>
            <div>
              <h1 className="text-lg md:text-2xl font-bold text-white">MD Link Dashboard</h1>
              <p className="hidden md:block text-xs md:text-sm text-blue-100">
                {managingBodega ? 
                  `Gestionando: ${availableBodegas.find(b => b.codigo === managingBodega)?.nombre || managingBodega}` :
                  (userInfo?.role === 'admin' ? 
                    'Centro de Control Ejecutivo' : 
                    `${getBodegaFullName(userInfo?.bodega)}`
                  )
                }
              </p>
            </div>
          </div>
          
          {/* Dashboard Controls */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Refresh Button - Hidden on mobile */}
            <div className="hidden md:flex flex-col items-end gap-1">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 px-2 py-1 bg-green-500/20 border border-green-400/30 rounded-md">
                  <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-[10px] text-green-200 font-medium">Auto-refresh: 5 min</span>
                </div>
                <Button 
                  onClick={handleManualRefresh}
                  disabled={isRefreshing}
                  variant="outline" 
                  size="sm"
                  className="border-white/30 text-white hover:bg-white/20 hover:text-white hover:border-white/50 transition-all duration-200 h-8"
                >
                  <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${isRefreshing ? 'animate-spin' : ''}`} />
                  {isRefreshing ? 'Actualizando...' : 'Actualizar'}
                </Button>
              </div>
              <span className="text-[10px] text-blue-200/80">
                Última actualización: {lastUpdate.toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            
            {/* Mobile Refresh Button */}
            <Button 
              onClick={handleManualRefresh}
              disabled={isRefreshing}
              variant="outline" 
              size="sm"
              className="md:hidden border-white/30 text-white hover:bg-white/20 hover:text-white hover:border-white/50 transition-all duration-200 h-9"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </Button>
            
            <div className="hidden md:flex items-center gap-3 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
              <span className="text-xs text-blue-100 font-medium">Powered by</span>
              <img 
                src="/mdlink-logo.png" 
                alt="MD Link" 
                className="h-7 w-auto object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'inline';
                }}
              />
              <span className="text-xs font-bold text-white" style={{display: 'none'}}>MD Link</span>
            </div>
            
            <div className="hidden md:block text-right">
              <div className="text-sm font-bold text-white">{userInfo?.name || getBodegaFullName(userInfo?.bodega)}</div>
              <div className="text-xs text-blue-200">
                {userInfo?.role === 'admin' ? 'Multi-Site Director' : `Site Manager`}
              </div>
            </div>
            <Button 
              onClick={onLogout} 
              variant="outline" 
              size="sm"
              className="border-white/30 text-white hover:bg-white/20 hover:text-white hover:border-white/50 transition-all duration-200"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden md:inline ml-2">Sign Out</span>
            </Button>
          </div>
        </div>
      </div>

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="max-w-7xl mx-auto space-y-6">

            {/* Dashboard Content */}
            {activeTab === 'dashboard' && (
              <div className="space-y-6">
            <DashboardStats 
              stats={stats} 
              fetchProductsByCategory={fetchProductsByCategory}
              onCheckCriticalStock={handleCheckCriticalStock}
              onSyncBitua={handleSyncBitua}
              userInfo={userInfo}
              managingBodega={managingBodega}
              onShowExpiryModal={() => setShowExpiryModalDashboard(true)}
            />
            <TopSkusSection data={topSkus} />
              </div>
            )}

            {/* Farmacia Dashboard - Para usuarios de farmacia */}
            {activeTab === 'farmacia' && (
              <FarmaciaDashboard 
                token={token}
                userInfo={userInfo}
                bodegaCodigo={userInfo?.bodega}
                activeFilter={farmaciaActiveFilter}
              />
            )}

            {/* Reports Content */}
            {activeTab === 'reports' && (
              <div className="space-y-6">
                <InventoryRisk riskItems={riskItems} />
                <DetailedInventory detailedItems={detailedItems} />
              </div>
            )}

            {/* Analytics Content */}
            {activeTab === 'analytics' && (
              <div className="space-y-6">
                <MinimosAnalysis data={minimosAnalysis} />
                <ConsumptionAnalysis 
                  data={consumptionAnalysis} 
                  timeFilter={consumptionTimeFilter}
                  setTimeFilter={setConsumptionTimeFilter}
                />
              </div>
            )}

            {/* Critical Items Content */}
            {activeTab === 'critical' && (
              <div className="space-y-6">
                <CriticalItemsAnalysis data={criticalItems} />
              </div>
            )}

            {/* Expiry Risk Content */}
            {activeTab === 'expiry-risk' && (
              <div className="space-y-6">
                <ExpiryRiskSection riskItems={riskItems} onRefresh={fetchDashboardData} />
              </div>
            )}

            {/* Reposiciones Content */}
            {activeTab === 'reposiciones' && (
              <div className="space-y-6">
                <ReposicionesSection token={token} userInfo={userInfo} />
              </div>
            )}

            {/* Charts Content */}
            {activeTab === 'charts' && (
              <div className="space-y-6">
                <ChartsSection 
                  stats={stats}
                  minimosAnalysis={minimosAnalysis}
                  consumptionAnalysis={consumptionAnalysis}
                  criticalItems={criticalItems}
                  topSkus={topSkus}
                  replenishmentData={replenishmentData}
                />
              </div>
            )}

            {/* Admin Content */}
            {activeTab === 'admin' && userInfo && (userInfo.role === 'admin' || userInfo.role === 'administrador') && !managingBodega && (
              <div className="space-y-6">
                <AdminSection 
                  token={token} 
                  userInfo={userInfo} 
                  onManageBodega={handleManageBodega}
                  availableBodegas={availableBodegas}
                />
              </div>
            )}

            {/* Historial Consigna (admin only) */}
            {activeTab === 'historial-consigna' && userInfo && (userInfo.role === 'admin' || userInfo.role === 'administrador') && !managingBodega && (
              <div className="space-y-6">
                <HistorialConsigna token={token} />
              </div>
            )}
          </div>
        </main>
      </div>
      
      {/* Modal para productos por categoría */}
      <ProductsByCategoryModal
        isOpen={categoryModal.isOpen}
        onClose={() => setCategoryModal(prev => ({ ...prev, isOpen: false }))}
        category={categoryModal.category}
        products={categoryModal.products}
        loading={categoryModal.loading}
      />

      {/* Modal de confirmación con contraseña */}
      <Dialog open={passwordModal.isOpen} onOpenChange={handlePasswordCancel}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-800 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              {passwordModal.title}
            </DialogTitle>
            <DialogDescription className="text-slate-600 whitespace-pre-line">
              {passwordModal.description}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Input
                type="password"
                placeholder="Ingrese la contraseña de seguridad"
                value={passwordModal.password}
                onChange={(e) =>
                  setPasswordModal(prev => ({ ...prev, password: e.target.value }))
                }
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handlePasswordSubmit();
                  }
                }}
                className="w-full"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={handlePasswordCancel}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handlePasswordSubmit}
                className="bg-red-600 hover:bg-red-700"
              >
                Confirmar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de vencimientos desde Dashboard */}
      <ExpiryAlertModal 
        token={token} 
        isOpen={showExpiryModalDashboard} 
        onClose={() => setShowExpiryModalDashboard(false)}
        bodega={managingBodega || userInfo?.bodega || 'N/A'}
      />
    </div>
  );
};

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [userInfo, setUserInfo] = useState(null);
  const [showExpiryModal, setShowExpiryModal] = useState(false);

  const handleLogin = (loginResponse) => {
    setToken(loginResponse.access_token);
    setUserInfo(loginResponse.user_info);
    localStorage.setItem('token', loginResponse.access_token);
    localStorage.setItem('userInfo', JSON.stringify(loginResponse.user_info));
    // Mostrar modal de vencimientos después del login
    setTimeout(() => setShowExpiryModal(true), 1500);
  };



  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');
    setToken(null);
    setUserInfo(null);
    setShowExpiryModal(false);
  };

  // Load user info from localStorage on app start
  useEffect(() => {
    try {
      const savedUserInfo = localStorage.getItem('userInfo');
      if (savedUserInfo && token) {
        const parsed = JSON.parse(savedUserInfo);
        if (parsed && typeof parsed === 'object') {
          setUserInfo(parsed);
        }
      }
    } catch (e) {
      console.error('Error parsing userInfo from localStorage:', e);
      localStorage.removeItem('userInfo');
      localStorage.removeItem('token');
      setToken(null);
    }
  }, [token]);

  return (
    <ErrorBoundary>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route
              path="/"
              element={
                token ? (
                  <Dashboard onLogout={handleLogout} userInfo={userInfo} token={token} />
                ) : (
                  <LoginPage onLogin={handleLogin} />
                )
              }
            />
          </Routes>
        </BrowserRouter>
        <Toaster />
        {token && <SupportChat token={token} />}
        {token && userInfo && (
          <ExpiryAlertModal 
            token={token} 
            isOpen={showExpiryModal} 
            onClose={() => setShowExpiryModal(false)}
            bodega={userInfo?.bodega || 'N/A'}
          />
        )}
      </div>
    </ErrorBoundary>
  );
}

export default App;